import React, { useState, useEffect } from 'react';
import { dateStr, localToday } from '../utils/dates';
import { groupDuplicateAthletes } from '../utils/duplicates';
import { coachHours, sessionCount } from '../utils/coachHours';
import app, { auth, db, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, deleteDoc, doc, getDoc, query, where, updateDoc, addDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import Layout from '../components/Layout';
import CompanyPanel from '../components/CompanyPanel';
import CustomerEditDialog from '../components/CustomerEditDialog';
import { logError } from '../utils/errorLogger';
import { logEvent } from '../utils/logEvent';
import { hasActiveDayPass, showsMemberTier, membershipStatus } from '../utils/membership';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Chip, CircularProgress, Avatar, Button, Tabs, Tab,
  Menu, MenuItem, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Select, FormControl, InputLabel, Divider,
  Radio, RadioGroup, FormControlLabel, Checkbox, Switch, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalculateIcon from '@mui/icons-material/Calculate';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { useNavigate } from 'react-router-dom';
import { getRoleChipsConfig } from '../utils/memberChips';
import { isComped } from '../utils/comp';

const ALL_TABS = ['All', 'Juniors', 'Members', 'Parents', 'Coaches', 'Leads', 'Volunteers', 'Players', 'Customers', 'Subscriptions', 'Archived'];
const PARENT_TABS = ['Juniors', 'Parents'];
// A member manager is here for one thing: to see who is a member and what
// state their membership is in. Everything else on this page — coaches, leads,
// customers, archived records — is someone else's job, so the tab never
// appears rather than appearing and refusing.
const MEMBER_MANAGER_TABS = ['Members'];

const ADD_MENU = [
  { label: 'Athlete', role: 'athlete' },
  { label: 'Parent', role: 'parent' },
  { label: 'Coach', role: 'coach' },
  { label: 'Assistant Coach', role: 'assistant_coach' },
  { label: 'Lead', role: 'lead' },
  { label: 'Contractor', role: 'contractor' },
];

// Determine member type — derived from role/collection, NOT membership status.
// Membership is a separate concept: see getMembershipStatus below.
function getMemberType(person) {
  const roles = person.roles || [];
  if (person._collection === 'athletes') {
    return person.athleteType === 'adult' ? 'Adult Athlete' : 'Junior Athlete';
  }
  if (roles.includes('admin')) return 'Admin';
  if (roles.includes('coach')) return 'Coach';
  if (roles.includes('assistant_coach')) return 'Asst Coach';
  if (roles.includes('parent')) return 'Parent';
  if (roles.includes('volunteer')) return 'Volunteer';
  if (roles.includes('lead')) return 'Lead';
  // A day pass is what this person actually is, as far as the club is
  // concerned — but don't let it mask a staff or parent role.
  if (person.membership?.isDayPass) return 'Day Pass';
  if (roles.includes('player')) return 'Player';
  return 'Non-Member';
}

// A day pass makes someone a member for one day only: the day the gate
// consumed it, or — until the gate records consumption — the day it was
// bought. The pass is valid for DAY_PASS_VALID_DAYS (functions; now the day
// of purchase only); this decides only who the People page counts as a member.
function isDayPassToday(m) {
  const sec = m.consumedAt?.seconds ?? m.consumedAt?._seconds
    ?? m.purchasedAt?.seconds ?? m.purchasedAt?._seconds;
  return Boolean(sec) && dateStr(new Date(sec * 1000)) === localToday();
}

// Determine membership status (Active / Inactive / null)
// Active = currently has a valid membership (recurring with status=active,
//          OR one-time/day-pass within its expiration window)
// Inactive = previously had a membership, now expired or canceled
// Returns null for users who never had a membership
function getMembershipStatus(person) {
  const m = person.membership;
  if (!m || !m.tierId) return null;

  // A day-passer was a member for that one day, never an ex-member: null keeps
  // them off the Members tab afterwards, even with inactive members shown.
  if (m.isDayPass === true) return isDayPassToday(m) ? 'Active' : null;

  // One-time / annual purchase: active until expiresAt passes.
  if (m.pricingMode === 'one_time') {
    const expSeconds = m.expiresAt?.seconds ?? m.expiresAt?._seconds;
    if (!expSeconds) {
      // No expiration set — treat as active (e.g. legacy data or just purchased)
      return m.status === 'canceled' ? 'Inactive' : 'Active';
    }
    return (expSeconds * 1000) > Date.now() ? 'Active' : 'Inactive';
  }

  // Recurring subscription path. 'trialing' = prepaid-period members (annuals
  // billed upfront) and 'frozen' = temporarily paused — both are CURRENT
  // members and belong on the Members tab.
  if (['active', 'trialing', 'frozen'].includes(m.status)) return 'Active';
  if (m.status === 'canceled') return 'Inactive';
  // past_due / unpaid — treat as Inactive for display purposes
  return 'Inactive';
}

// Determine status for the Status column.
// Priority: Pending link > manual admin override (active === false) >
// membership expiry (day pass / one-time: Expired once expiresAt passes;
// canceled shows Canceled; past_due shows Past Due; frozen shows Frozen; any of
// canceled / frozen / expired for 90+ days shows Inactive) > Active.
// Note: this is computed at read time — the Firestore membership.status field
// itself still says 'active'; the column just reflects reality by the clock.
// Canceled, frozen or expired for this long reads as Inactive: gone, not
// between payments. Measured from the day access ended (canceled, expired) or
// the day the freeze began (frozen).
const INACTIVE_AFTER_DAYS = 90;
// Firestore Timestamp, a {seconds} map, an ISO string or a 'YYYY-MM-DD' date.
const _msOf = (v) => {
  if (!v) return NaN;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const sec = v.seconds ?? v._seconds;
  if (sec != null) return sec * 1000;
  return new Date(v).getTime();
};
const _longGone = (v) => (Date.now() - _msOf(v)) >= INACTIVE_AFTER_DAYS * 864e5;

function getStatus(person) {
  if (person.pendingLinkEmail) return 'Pending';
  if (person.active === false) return 'Inactive';
  const m = person.membership;
  if (m && m.tierId) {
    if (m.isDayPass === true) return isDayPassToday(m) ? 'Active' : 'Inactive';
    const expSeconds = m.expiresAt?.seconds ?? m.expiresAt?._seconds;
    if (expSeconds && expSeconds * 1000 <= Date.now()) {
      return _longGone(m.expiresAt) ? 'Inactive' : 'Expired';
    }
    if (m.status === 'canceled') {
      // The last day they had access, by the same precedence the gate uses;
      // with no date at all there is nothing to age, so it stays Canceled.
      const ended = m.accessUntil || m.cancelAt || m.currentPeriodEnd || m.canceledAt || m.accessEndedAt;
      return ended && _longGone(ended) ? 'Inactive' : 'Canceled';
    }
    // Still a member, but the payment is failing — surface it rather than
    // letting it read as a healthy Active row.
    if (m.status === 'past_due') return 'Past Due';
    // Paused, not paying: no booking and no gate. Reading Active here hid a
    // Stripe pause for months; paused for 90+ days is a member who has gone.
    if (m.status === 'frozen') return m.freeze?.startsAt && _longGone(m.freeze.startsAt) ? 'Inactive' : 'Frozen';
    return 'Active';
  }
  return 'Active';
}

const MEMBER_TYPE_COLORS = {
  'Junior Athlete': { bg: '#E3F2FD', color: '#0D47A1' },
  'Adult Athlete':  { bg: '#E8F5E9', color: '#1B5E20' },
  'Parent':         { bg: '#FFF3E0', color: '#BF360C' },
  'Coach':          { bg: '#F3E5F5', color: '#4A148C' },
  'Asst Coach':     { bg: '#F3E5F5', color: '#6A1B9A' },
  'Admin':          { bg: '#FFEBEE', color: '#B71C1C' },
  'Volunteer':      { bg: '#E0F7FA', color: '#006064' },
  'Lead':           { bg: '#FFF9C4', color: '#827717' },
  'Player':         { bg: '#F1F8E9', color: '#33691E' },
  'Non-Member':     { bg: '#F4F6F7', color: '#555' },
};

// Role-chip styling — mirrors Profile.js so chips look identical across the app.
const ROLE_COLORS = {
  admin: { bg: '#FDEDEC', color: '#922B21' },
  coach: { bg: '#EBF5FB', color: '#1A5276' },
  assistant_coach: { bg: '#EBF5FB', color: '#1A5276' },
  volunteer: { bg: '#F4F6F7', color: '#555' },
  parent: { bg: '#E9F7EF', color: '#1E6B3C' },
  adult_athlete: { bg: '#FEF9E7', color: '#7B4800' },
  junior_athlete: { bg: '#F5EEF8', color: '#6C3483' },
  member: { bg: '#FEF5D7', color: '#7E6010' },
  renter: { bg: '#E0F2F1', color: '#00695C' },
  lead: { bg: '#FDF2E9', color: '#A04000' },
  player: { bg: '#EBF5FB', color: '#21618C' },
  contractor: { bg: '#E8F8F5', color: '#0E6251' },
};
const ROLE_LABELS = {
  admin: 'Admin', coach: 'Coach', assistant_coach: 'Assistant Coach',
  volunteer: 'Volunteer', parent: 'Parent', adult_athlete: 'Adult Athlete',
  junior_athlete: 'Junior Athlete', member: 'Member', renter: 'Renter', lead: 'Lead', player: 'Player',
  contractor: 'Contractor', member_manager: 'Member Manager', court_admin: 'Calendar Manager',
};

// Tier-name → visual style for membership badge. Match is case-insensitive
// "contains" so "Gold Membership - Annual" still maps to GOLD.


const STATUS_COLORS = {
  'Pending':  { bg: '#FEF9E7', color: '#B7950B' },
  'Active':   { bg: '#E9F7EF', color: '#1E6B3C' },
  'Inactive': { bg: '#F4F6F7', color: '#555' },
  'Expired':  { bg: '#FDF2E9', color: '#9C4E0E' },
  'Past Due': { bg: '#FDEDEC', color: '#922B21' },
  'Frozen':   { bg: '#EAECEE', color: '#566573' },
  'Canceled': { bg: '#FBEEE6', color: '#A04000' },
  'Alert':    { bg: '#FDEDEC', color: '#922B21' },
};

function getInitials(first, last) {
  return `${first?.charAt(0) || ''}${last?.charAt(0) || ''}`.toUpperCase();
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts.seconds * 1000).toLocaleDateString();
}

function formatSessionType(sessionType) {
  const formatted = sessionType ? sessionType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—';
  // Clean up specific session types
  return formatted
    .replace('Junior Practice', 'Junior Practice')
    .replace('Adult Practice', 'Adult Practice')
    .replace('Junior Private', 'Junior Private')
    .replace('Junior Drop In', 'Junior Drop-In')
    .replace('Junior Evaluation', 'Junior Evaluation');
}

function People() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [allPeople, setAllPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [showInactiveMembers, setShowInactiveMembers] = useState(false);
  // Customers (organizations renting court time). The add/edit form and its
  // billing-rate rules live in CustomerEditDialog, shared with the Company tab
  // of a renter's profile.
  const [customers, setCustomers] = useState([]);
  const [customerDialog, setCustomerDialog] = useState(false);
  const [customerEditTarget, setCustomerEditTarget] = useState(null); // null = new
  const [customerDeleteTarget, setCustomerDeleteTarget] = useState(null);
  const [customerMenuAnchor, setCustomerMenuAnchor] = useState(null);
  const [customerMenuTarget, setCustomerMenuTarget] = useState(null);
  const [customerDetailTarget, setCustomerDetailTarget] = useState(null);
  const [showArchivedCustomers, setShowArchivedCustomers] = useState(false);
  const [playerRegs, setPlayerRegs] = useState([]); // tournament registrations for Players tab
  const [addAnchor, setAddAnchor] = useState(null);
  const [rowAnchor, setRowAnchor] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [uploadingPhotoId, setUploadingPhotoId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMemberManager, setIsMemberManager] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [payCoach, setPayCoach] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'Venmo', note: '' });
  const [paySuccess, setPaySuccess] = useState('');
  const [coachSummary, setCoachSummary] = useState({ totalOwed: 0, paidThisMonth: 0, paidYTD: 0 });
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Duplicate finder
  const [dupsDialog, setDupsDialog] = useState(false);
  const [dupsLoading, setDupsLoading] = useState(false);
  const [dupsGroups, setDupsGroups] = useState([]); // [{key, docs: [{...athlete, _attendanceCount}]}]
  const [dupsKeeper, setDupsKeeper] = useState({}); // { groupKey: athleteIdToKeep }
  const [dupsMerging, setDupsMerging] = useState(false);
  const [dupsMsg, setDupsMsg] = useState('');
  const [calcDialog, setCalcDialog] = useState(false);
  const [calcCoach, setCalcCoach] = useState(null);
  const [calcSessions, setCalcSessions] = useState({ head: 0, assistant: 0, headHours: 0, assistantHours: 0 });
  const [calcSaving, setCalcSaving] = useState(false);
  const [paySaving, setPaySaving] = useState(false);
  const [coachDetailDialog, setCoachDetailDialog] = useState(false);
  const [memberDetailDialog, setMemberDetailDialog] = useState(false);
  const [dayPassCredits, setDayPassCredits] = useState([]);
  const [creditReason, setCreditReason] = useState('');
  const [creditBusy, setCreditBusy] = useState(false);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberBusy, setMemberBusy] = useState(false);
  const [memberMsg, setMemberMsg] = useState(null);
  const [memberFreezeMonths, setMemberFreezeMonths] = useState(1);
  const [allPurchases, setAllPurchases] = useState([]);
  const [coachDetailPerson, setCoachDetailPerson] = useState(null);
  const [coachDetailClasses, setCoachDetailClasses] = useState([]);
  const [coachClassAttendees, setCoachClassAttendees] = useState({});
  const [coachDetailPayments, setCoachDetailPayments] = useState([]);
  const [coachDetailLoading, setCoachDetailLoading] = useState(false);
  const [coachClassesSincePayment, setCoachClassesSincePayment] = useState(0);
  const [coachWorkEntries, setCoachWorkEntries] = useState([]);
  const [coachClassCounts, setCoachClassCounts] = useState({ head: 0, assistant: 0, private: 0 });
  const [coachPaidThisMonth, setCoachPaidThisMonth] = useState(0);
  const [coachPaidYTD, setCoachPaidYTD] = useState(0);
  // ── Contractor detail + work log ──
  const [contractorDialog, setContractorDialog] = useState(false);
  const [contractorPerson, setContractorPerson] = useState(null);
  const [contractorEntries, setContractorEntries] = useState([]);
  const [contractorPayments, setContractorPayments] = useState([]);
  const [contractorLoading, setContractorLoading] = useState(false);
  const [contractorRate, setContractorRate] = useState('');
  const [rateSaving, setRateSaving] = useState(false);
  const [contractorPayType, setContractorPayType] = useState('per_job');
  const [workEditId, setWorkEditId] = useState(null);
  const [logWorkOpen, setLogWorkOpen] = useState(false);
  const [logWorkSaving, setLogWorkSaving] = useState(false);
  const [selectedEntryIds, setSelectedEntryIds] = useState({});
  const [payWorkOpen, setPayWorkOpen] = useState(false);
  const [payWorkForm, setPayWorkForm] = useState({ method: 'Venmo', note: '', datePaid: '' });
  const [payWorkSaving, setPayWorkSaving] = useState(false);
  const [workForm, setWorkForm] = useState({ type: 'hours', hours: '', rate: '', amount: '', description: '', date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })() });
  // Selected classes in the coach-detail pay flow (maps classId -> true)
  const [selectedClassIds, setSelectedClassIds] = useState({});
  // Set of classIds that have already been paid (from prior payment records)
  const [paidClassIds, setPaidClassIds] = useState(new Set());
  const [selectedWorkIds, setSelectedWorkIds] = useState({});
  const [detailPayForm, setDetailPayForm] = useState({ amount: '', method: 'Venmo', datePaid: '', note: '' });
  const [detailPaySaving, setDetailPaySaving] = useState(false);
  const [detailPaySuccess, setDetailPaySuccess] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [filter, setFilter] = useState('');
  const [rolesDialog, setRolesDialog] = useState(false);
  const [rolesTarget, setRolesTarget] = useState(null);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Athlete/Parent detail dialog and session history
  const [athleteDetailDialog, setAthleteDetailDialog] = useState(false);
  const [athleteDetailPerson, setAthleteDetailPerson] = useState(null);
  const [athleteDetailLoading, setAthleteDetailLoading] = useState(false);
  const [athleteSessionHistory, setAthleteSessionHistory] = useState([]);

 useEffect(() => {
  const fetchAll = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};
    const roles = userData.roles || [];
    const admin = roles.includes('admin');
    const coach = (roles.includes('coach') || roles.includes('assistant_coach')) && !admin;
    // Read-only access to the member list. Never combined with admin: an admin
    // who also held the role would otherwise lose their own edit controls.
    const memberManager = roles.includes('member_manager') && !admin;
    setIsAdmin(admin);
    setIsCoach(coach);
    setIsMemberManager(memberManager);
    setRolesLoaded(true);
    if (!admin && !coach && !memberManager) {
      // Redirect parents/athletes directly to their profile page
      navigate('/profile', { replace: true });
      return;
      const ownSnap = await getDocs(query(collection(db, 'athletes'), where('email', '==', user.email)));
      const juniorSnap = await getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', user.uid)));
      const allLinked = [
        ...ownSnap.docs.map(d => ({ id: d.id, _collection: 'athletes', ...d.data() })),
        ...juniorSnap.docs.map(d => ({ id: d.id, _collection: 'athletes', ...d.data() })),
      ];
      // Enrich user record: hasPurchased if any linked athlete has sessionBalance > 0
      const totalBalance = allLinked.reduce((sum, a) => sum + (a.sessionBalance || 0), 0);
      const enrichedUser = {
        id: user.uid,
        _collection: 'users',
        ...userData,
        sessionBalance: totalBalance,
        hasPurchased: totalBalance > 0,
      };
      setAllPeople([enrichedUser, ...allLinked]);
      setLoading(false);
      return;
    }
      const athleteSnap = await getDocs(collection(db, 'athletes'));
      const athletes = athleteSnap.docs.map(d => ({ id: d.id, _collection: 'athletes', ...d.data() }));
      const userSnap = await getDocs(collection(db, 'users'));
      const users = userSnap.docs.map(d => ({ id: d.id, _collection: 'users', ...d.data() }));
      // Customers tab data (organizations renting court time). The collection
      // is admin-only in firestore.rules and only admins get the tab, so
      // coaches and member managers skip the read rather than log a denial.
      if (admin) {
        try {
          const custSnap = await getDocs(collection(db, 'customers'));
          setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.organizationName || '').localeCompare(b.organizationName || '')));
        } catch (e) {
          logError('People', 'customers load: ' + (e.message || String(e)), e.stack || '');
        }
      }
      const coaches = [];
      // Enrich athletes with parent/user data for contact info
      const enrichedAthletes = athletes.map(a => {
        if (a.parentUserId) {
          // Junior — enrich from parent user doc
          const parent = users.find(u => u.id === a.parentUserId || u.uid === a.parentUserId);
          return parent
            ? { ...a, parentName: `${parent.firstName || ''} ${parent.lastName || ''}`.trim(), parentEmail: parent.email || '', email: a.email || parent.email || '' }
            : a;
        } else {
          // Adult — match to user doc by email only (name matching is too broad)
          const userMatch = users.find(u =>
            a.email && u.email && a.email.toLowerCase() === u.email.toLowerCase()
          );
          return userMatch
            ? {
                ...a,
                email: a.email || userMatch.email || '',
                phone: a.phone || userMatch.phone || '',
                address: a.address || userMatch.address || '',
                // Pass membership through so adult athletes show member badges,
                // and remember WHICH user doc it came from so the member detail
                // dialog can act on the right account (manageMembership targetUserId).
                _memberUserId: userMatch.id,
                ...(userMatch.membership ? { membership: userMatch.membership } : {}),
                // Pass user's roles through for label classification (coach/admin/parent etc on adult athletes)
                ...(userMatch.roles ? { _userRoles: userMatch.roles } : {}),
              }
            : a;
        }
      });
      const enrichedUsers = users
        // A merged login leaves a pointer doc behind: the second account's uid,
        // its email, and mergedIntoUid naming the account it resolves to. It
        // carries no name because it is not a person — the person is the
        // canonical doc, which is already in this list. Six of them were
        // showing as blank rows on the All tab.
        //
        // Filtered here rather than out of `users` above, which is still used
        // to enrich athletes by parent id and by email: dropping the stubs
        // there could remove a match instead of a row. Register.js,
        // AdminGate.js and MembershipPanel.js all skip them the same way.
        .filter(u => !u.mergedIntoUid)
        // Pending accounts are hidden because linking a guardian by email
        // creates placeholder parent stubs that would clutter the list. One an
        // admin typed in via Add Person is not a stub — hiding it made the
        // person vanish the moment they were added, so keep those visible.
        // getStatus() already labels them 'Pending'.
        .filter(u => !u.pendingLinkEmail
          || u.createdByAdmin
          || (u.roles || []).some(r => ['coach', 'assistant_coach', 'contractor'].includes(r)))
        .map(u => {
          const isParent = (u.roles || []).includes('parent') && !(u.roles || []).some(r => ['coach','assistant_coach','admin'].includes(r));
          if (isParent) {
            // Find all linked junior athletes
            const children = athletes.filter(a => a.parentUserId === u.id || a.parentUserId === u.uid);
            if (children.length > 0) {
              const minBalance = Math.min(...children.map(a => a.sessionBalance ?? 0));
              const latestCheckIn = children.reduce((latest, a) => {
                const ts = a.lastCheckIn?.seconds ?? 0;
                return ts > latest ? ts : latest;
              }, 0);
              return { ...u, sessionBalance: minBalance, _childCount: children.length, _latestChildCheckIn: latestCheckIn };
            }
            return u;
          }
          // Find linked athlete by (in order): email match, parentUserId,
          // explicit userId field, or embedded UID in athlete doc ID
          // (`ath_{uid}_{ts}` from adult registration).
          const la = athletes.find(a => {
            if (a.email && u.email && a.email.toLowerCase().trim() === u.email.toLowerCase().trim()) return true;
            if (a.parentUserId && (a.parentUserId === u.id || a.parentUserId === u.uid)) return true;
            if (a.userId && a.userId === u.id) return true;
            if (typeof a.id === 'string' && a.id.startsWith('ath_')) {
              const rest = a.id.slice(4);
              const idx = rest.lastIndexOf('_');
              const embeddedUid = idx > 0 ? rest.slice(0, idx) : null;
              if (embeddedUid && embeddedUid === u.id) return true;
            }
            return false;
          });
          return la ? { ...u, sessionBalance: la.sessionBalance, _athleteId: la.athleteId || la.id } : u;
        });

      // Enrich coaches with _outstanding (dollar amount owed).
      //
      // coachPayments and workEntries are admin-only in firestore.rules, and
      // this Promise.all sits outside any try/catch — so for a coach or a
      // member manager the whole loader threw here and the page never left its
      // spinner, because setAllPeople and setLoading are both below. They feed
      // coach pay and contractor balances, which only appear on the admin-only
      // Coaches tab and pay summary, so a non-admin reads them as empty.
      const emptySnap = { docs: [] };
      const [classSnap, paySnap, purchaseSnap, attendanceSnap, workSnap] = await Promise.all([
        getDocs(collection(db, 'classes')),
        admin ? getDocs(collection(db, 'coachPayments')) : Promise.resolve(emptySnap),
        getDocs(collection(db, 'purchases')),
        getDocs(collection(db, 'attendance')),
        admin ? getDocs(collection(db, 'workEntries')) : Promise.resolve(emptySnap),
      ]);
      const unpaidWorkByWorker = new Map();
      workSnap.docs.forEach(d => {
        const w = d.data();
        if (w.paid || w.approved === false) return;
        unpaidWorkByWorker.set(w.workerId, (unpaidWorkByWorker.get(w.workerId) || 0) + (w.amount || 0));
      });
      setAllPurchases(purchaseSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      // Build set of athleteIds that have made purchases
      const purchasedAthleteIds = new Set(purchaseSnap.docs.map(d => d.data().athleteId).filter(Boolean));
      // ...and the user accounts that bought something themselves (memberships,
      // day passes, sessions bought under the account rather than an athlete)
      const purchasedUserIds = new Set(purchaseSnap.docs.map(d => d.data().userId).filter(Boolean));
      // Build map of athleteId → latest purchase (for plan name display)
      const latestPlanByAthlete = new Map();
      for (const pd of purchaseSnap.docs) {
        const p = pd.data();
        if (!p.athleteId) continue;
        const existing = latestPlanByAthlete.get(p.athleteId);
        const pTime = p.createdAt?.seconds || (p.purchaseDate ? new Date(p.purchaseDate).getTime() / 1000 : 0);
        const existingTime = existing?.createdAt?.seconds || (existing?.purchaseDate ? new Date(existing.purchaseDate).getTime() / 1000 : 0);
        if (!existing || pTime > existingTime) latestPlanByAthlete.set(p.athleteId, p);
      }
      // Build map of athleteId → latest attendance timestamp (for Last Check-in column)
      // Derived from attendance collection so historical check-ins are reflected even if lastCheckIn field was never written.
      const latestCheckInByAthlete = new Map();
      for (const ad of attendanceSnap.docs) {
        const a = ad.data();
        if (!a.athleteId || a.type === 'coach') continue;
        const seconds = a.checkedInAt?.seconds ?? (a.checkedInAt instanceof Date ? Math.floor(a.checkedInAt.getTime() / 1000) : 0);
        if (!seconds) continue;
        const existing = latestCheckInByAthlete.get(a.athleteId);
        if (!existing || seconds > existing) latestCheckInByAthlete.set(a.athleteId, seconds);
      }
      const today = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })();
      const allClasses = classSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.status !== 'canceled' && c.date <= today);
      const allPayments = paySnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Per session, not per document — a class covering two courts is stored
      // as two documents but is one coaching session. See utils/coachHours.js.
      const calcHours = coachHours;

      const enrichedWithClasses = enrichedUsers.map(u => {
        // Contractors aren't coaches but still need their unpaid-work total.
        if (!(u.roles || []).some(r => ['coach', 'assistant_coach'].includes(r))) {
          if ((u.roles || []).includes('contractor')) {
            return { ...u, _contractorOutstanding: unpaidWorkByWorker.get(u.id) || 0 };
          }
          return u;
        }
        const coachPayments = allPayments
          .filter(p => p.coachId === u.id)
          .sort((a, b) => (b.paidAt?.seconds || 0) - (a.paidAt?.seconds || 0));
        const lastPayment = coachPayments[0];
        const lastPaidDate = lastPayment
          ? (lastPayment.datePaid || (lastPayment.paidAt?.seconds
              ? dateStr(new Date(lastPayment.paidAt.seconds * 1000))
              : ''))
          : '';
        const headRate = parseFloat(u.headRatePerHour) || 0;
        const asstRate = parseFloat(u.assistantRatePerHour) || headRate;
        const unpaid = allClasses.filter(c =>
          (c.headCoachId === u.id || (c.assistantCoachIds || []).includes(u.id)) &&
          c.date > lastPaidDate
        );
        const unpaidHead    = unpaid.filter(c => c.headCoachId === u.id && c.sessionType !== 'junior-private');
        const unpaidAsst    = unpaid.filter(c => (c.assistantCoachIds || []).includes(u.id) && c.sessionType !== 'junior-private');
        const unpaidPrivate = unpaid.filter(c => c.sessionType === 'junior-private');
        const outstanding = Math.max(0,
          calcHours(unpaidHead)    * headRate +
          calcHours(unpaidAsst)    * asstRate +
          calcHours(unpaidPrivate) * headRate
        );

        // Coach "last check-in" = most recent class they coached (head or assistant).
        // Without this, the Last Check-In column is blank for coaches who don't
        // attend classes as athletes.
        let coachLastClass = null;
        const taughtClasses = allClasses.filter(c =>
          c.headCoachId === u.id || (c.assistantCoachIds || []).includes(u.id)
        );
        if (taughtClasses.length > 0) {
          const latest = taughtClasses.reduce((max, c) => {
            if (!c.date) return max;
            return (!max || c.date > max) ? c.date : max;
          }, null);
          if (latest && /^\d{4}-\d{2}-\d{2}$/.test(latest)) {
            const [y, m, d] = latest.split('-').map(Number);
            coachLastClass = { seconds: Math.floor(new Date(y, m - 1, d).getTime() / 1000) };
          }
        }

        const isContractor = (u.roles || []).includes('contractor');
        const contractorOutstanding = isContractor ? (unpaidWorkByWorker.get(u.id) || 0) : null;
        return {
          ...u,
          _classesSincePayment: outstanding,
          _contractorOutstanding: contractorOutstanding,
          lastCheckIn: u.lastCheckIn || coachLastClass,
        };
      });

      // Dedup: adult athletes whose email matches a user doc represent the same person
      // (the user doc handles login/billing, the athlete doc handles training data).
      // Dedup: a single adult shouldn't appear as both a user row and an athlete row.
      // Hide the athlete row from /people; the user row already represents them.
      // Juniors dedup the other way round — the athlete doc is the row that
      // survives, not the login stub. See the junior branch below.
      //
      // Match athlete → user in three ways (any match hides the athlete row):
      //   1. Email match (primary)
      //   2. Explicit `userId` field on athlete pointing to a user doc
      //   3. Athlete doc ID embeds the user UID (pattern: `ath_{uid}_{ts}` from
      //      adult registration) — this catches athletes whose `email` field
      //      was never written or got dropped.
      const userIds = new Set(enrichedWithClasses.map(u => u.id).filter(Boolean));
      const userIdByEmail = new Map(
        enrichedWithClasses
          .filter(u => (u.email || '').trim())
          .map(u => [(u.email || '').toLowerCase().trim(), u.id])
      );
      const extractUidFromAthleteId = (athleteId) => {
        if (!athleteId || !athleteId.startsWith('ath_')) return null;
        const rest = athleteId.slice(4);
        const lastUnderscore = rest.lastIndexOf('_');
        if (lastUnderscore <= 0) return null;
        return rest.slice(0, lastUnderscore);
      };
      // Map each merged adult's user UID -> their athlete doc id, so the user row
      // can deep-link to the athlete profile (Sessions/Purchases/Tournaments tabs).
      const athleteIdByUser = {};
      // A junior with their own login — rare, but it happens when someone
      // registers as an adult and is later corrected to junior, or signs up for
      // themselves. They too should list once, but it is the LOGIN STUB that
      // goes, not the athlete doc: everything the club looks up about a junior
      // (school, parents, waivers, session credits) lives on the athlete doc and
      // only /athletes/{id} renders it. Hiding the athlete row instead dropped
      // them off the Juniors tab and sent the All tab to a user profile showing
      // "No athlete record linked to this account", no parents and no credits.
      const juniorSelfUserIds = new Set();
      const dedupedAthletes = enrichedAthletes.filter(a => {
        const isAdult = (a.athleteType || '').toLowerCase() === 'adult';
        if (!isAdult) {
          // ONLY the explicit self-link counts here. The other two signals the
          // adult path uses both point at the PARENT for a parent-registered
          // junior: the athlete doc carries the parent's email, and its id
          // embeds the parent's uid (ath_{parentUid}_{ts}). Today that is 48 of
          // 60 juniors by id and 7 more by email — reusing those here would
          // hide most of the roster instead of one duplicate.
          if (a.userId && userIds.has(a.userId) && a.userId !== a.parentUserId) {
            juniorSelfUserIds.add(a.userId);
          }
          return true;
        }
        const aEmail = (a.email || '').toLowerCase().trim();
        let matchedUid = null;
        if (aEmail && userIdByEmail.has(aEmail)) matchedUid = userIdByEmail.get(aEmail);
        else if (a.userId && userIds.has(a.userId)) matchedUid = a.userId;
        else {
          const embeddedUid = extractUidFromAthleteId(a.id);
          if (embeddedUid && userIds.has(embeddedUid)) matchedUid = embeddedUid;
        }
        if (matchedUid) {
          if (!athleteIdByUser[matchedUid]) athleteIdByUser[matchedUid] = a.id;
          return false; // hide athlete row; the user row represents this adult
        }
        return true;
      });
      const userById = new Map(enrichedWithClasses.map(u => [u.id, u]));

      setAllPeople([...dedupedAthletes.map(a => {
        const latestPurchase = latestPlanByAthlete.get(a.id) || latestPlanByAthlete.get(a.athleteId);
        // Merge attendance-derived lastCheckIn with any stored lastCheckIn — take the most recent
        const derivedSec = latestCheckInByAthlete.get(a.id) || latestCheckInByAthlete.get(a.athleteId) || 0;
        const storedSec = a.lastCheckIn?.seconds || 0;
        const bestSec = Math.max(derivedSec, storedSec);
        const lastCheckIn = bestSec ? { seconds: bestSec } : a.lastCheckIn;
        // A junior whose own login stub was dropped above still owns whatever
        // membership sat on that account — carry it onto this row, and remember
        // which user doc it came from so the member dialog manages the account
        // rather than the athlete record.
        const selfUser = juniorSelfUserIds.has(a.userId) ? userById.get(a.userId) : null;
        return {
          ...a,
          ...(selfUser ? {
            _memberUserId: selfUser.id,
            ...(selfUser.membership ? { membership: selfUser.membership } : {}),
          } : {}),
          hasPurchased: a.hasPurchased || purchasedAthleteIds.has(a.id) || purchasedAthleteIds.has(a.athleteId),
          planName: a.planName || latestPurchase?.planName || '',
          lastCheckIn,
        };
      }), ...enrichedWithClasses.filter(u => !juniorSelfUserIds.has(u.id)).map(u => {
        const _athleteId = athleteIdByUser[u.id];
        const boughtSelf = Boolean(u.hasPurchased) || purchasedUserIds.has(u.id);
        if (!(u.roles || []).includes('lead')) {
          return { ...u, hasPurchased: boughtSelf, ...(_athleteId ? { _athleteId } : {}) };
        }
        // Mark lead as hasPurchased if any linked athlete has a purchase
        const linkedAthletes = enrichedAthletes.filter(a =>
          a.parentUserId === u.id || a.email === u.email || a.userId === u.id
        );
        const purchased = boughtSelf || linkedAthletes.some(a =>
          purchasedAthleteIds.has(a.id) || purchasedAthleteIds.has(a.athleteId) || (a.sessionBalance || 0) > 0
        );
        return { ...u, hasPurchased: purchased, ...(_athleteId ? { _athleteId } : {}) };
      })]);

      // Page-level coach payment summary
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      // Combined owed across coaches (class + folded work) and contractors (work).
      const totalOwed = enrichedWithClasses.reduce((s, u) =>
        s + (u._classesSincePayment || 0) + (u._contractorOutstanding || 0), 0);
      const paidThisMonth = allPayments
        .filter(p => { const d = p.paidAt?.seconds ? new Date(p.paidAt.seconds * 1000) : null; return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear; })
        .reduce((s, p) => s + (p.amount || 0), 0);
      const paidYTD = allPayments
        .filter(p => { const d = p.paidAt?.seconds ? new Date(p.paidAt.seconds * 1000) : null; return d && d.getFullYear() === thisYear; })
        .reduce((s, p) => s + (p.amount || 0), 0);
      setCoachSummary({ totalOwed, paidThisMonth, paidYTD });

      setLoading(false);
  };
  fetchAll();
}, []);

  // (Coach auto-open removed — coaches now see their own account at /my-account)

  // Filter by tab
  // Every control that writes is gated on this rather than on isAdmin, so a
  // coach keeps exactly what they had and only the new role is held back.
  // The server refuses their writes regardless — users and athletes both
  // require admin in firestore.rules, and manageMembership re-checks the
  // caller — so this decides what is SHOWN, not what is permitted.
  const canEditPeople = !isMemberManager;
  const TABS = isAdmin ? ALL_TABS : isMemberManager ? MEMBER_MANAGER_TABS : PARENT_TABS;

  const getTabPeople = () => {
    const label = TABS[tab];
    switch (label) {
      case 'All':
        // Every record, except archived (admins see archived on its own tab)
        return allPeople.filter(p => !p.isArchived);
      case 'Juniors':
        // Adult athletes matched to a user account are deduped out upstream —
        // their user row represents them — so this is juniors in practice.
        return allPeople.filter(p => p._collection === 'athletes' && !p.isArchived);
      case 'Members':
        // Members = anyone subscribed to a recurring membership tier.
        // Active by default; inactive included when showInactiveMembers is true.
        return allPeople.filter(p => {
          if (p.isArchived) return false;
          const ms = getMembershipStatus(p);
          if (!ms) return false;
          return showInactiveMembers ? true : ms === 'Active';
        });
      case 'Parents':
        return allPeople.filter(p => p._collection === 'users' && (p.roles || []).includes('parent') && !p.isArchived);
      case 'Coaches':
        // Staff tab: coaches, contractors, admins, and the two limited staff
        // roles (Calendar Manager, Member Manager)
        return allPeople.filter(p => (p._collection === 'users' && (p.roles || []).some(r => ['coach', 'assistant_coach', 'contractor', 'admin', 'court_admin', 'member_manager'].includes(r))) && !p.isArchived);
      case 'Leads':
        // Leads = registered, and has never bought a pass, membership or session.
        // Once they have — even a day pass long since used — they stop being a lead.
        // Excludes: anyone with the 'player' role (tournament participants),
        //   anyone with a session balance > 0, anyone marked hasPurchased
        //   (a purchase under their account or a linked athlete), anyone who
        //   has ever held a membership or day pass, anyone with a check-in
        //   record (already attended a class), archived users, and staff.
        return allPeople.filter(p => {
          if (p.isArchived) return false;
          if (p.hasPurchased) return false;
          if (p.membership?.tierId || p.dayPass) return false;
          if ((p.sessionBalance || 0) > 0) return false;
          if (p.lastCheckIn) return false;          // attended at least one class
          if (p._latestChildCheckIn) return false;  // parent of an athlete who has attended
          const roles = p.roles || [];
          if (roles.includes('player')) return false;
          if (p._collection === 'athletes') {
            return !p.isPromotedToActive;
          }
          if (p._collection === 'users') {
            // Exclude staff (admins/coaches/volunteers) — they're not "leads" even without purchases
            if (roles.some(r => ['admin', 'coach', 'assistant_coach', 'volunteer'].includes(r))) return false;
            return true;
          }
          return false;
        });
      case 'Volunteers':
        return allPeople.filter(p => p._collection === 'users' && (p.roles || []).includes('volunteer') && !p.isArchived);
      case 'Players':
        return allPeople.filter(p =>
          (p.roles || []).includes('player') && !p.isArchived
        );
      case 'Subscriptions':
        return allPeople.filter(p =>
          p._collection === 'athletes' && p.subscriptionStatus && !p.isArchived
        );
      case 'Archived':
        return allPeople.filter(p => p.isArchived);
      default:
        return [];
    }
  };

  // The chips offered by the Manage Roles dialog. Distinct from MENU_ROLES in
  // config/menuIcons.js, which decides who sees a nav item — this decides what
  // an admin can hand out, and the two lists have to be kept in step by hand.
  //
  // A role missing from here is invisible rather than fragile: selectedRoles is
  // seeded from the person's existing roles and saved whole, so a role with no
  // chip survives an edit untouched. Several real roles are still absent —
  // adult_athlete, junior_athlete, member, renter, tournament_admin —
  // deliberately left that way for now, since 'member' in
  // particular grants booking rights and should not become a chip without
  // thinking about who gets to click it.
  const AVAILABLE_ROLES = ['admin', 'coach', 'assistant_coach', 'contractor', 'volunteer', 'parent', 'player', 'lead', 'member_manager', 'court_admin', 'hs_coach'];

  const handleManageRoles = () => {
    setRowAnchor(null);
    setSelectedRoles(selectedPerson?.roles || []);
    setRolesTarget(selectedPerson);
    setRolesDialog(true);
  };

  const handleSaveRoles = async () => {
    if (!rolesTarget) return;
    setRolesSaving(true);
    try {
      const collectionName = rolesTarget._collection === 'athletes' ? 'athletes' : 'users';
      const beforeRoles = rolesTarget.roles || [];
      await updateDoc(doc(db, collectionName, rolesTarget.id), { roles: selectedRoles });
      const added = selectedRoles.filter(r => !beforeRoles.includes(r));
      const removed = beforeRoles.filter(r => !selectedRoles.includes(r));
      if (added.length || removed.length) {
        logEvent('user.roles_changed', {
          target: {
            id: rolesTarget.id,
            email: rolesTarget.email || '',
            name: `${rolesTarget.firstName || ''} ${rolesTarget.lastName || ''}`.trim(),
          },
          description: `Roles changed${added.length ? ` — added: ${added.join(', ')}` : ''}${removed.length ? ` — removed: ${removed.join(', ')}` : ''}`,
          metadata: { before: beforeRoles, after: selectedRoles, collection: collectionName },
        });
      }
      setAllPeople(prev => prev.map(p => p.id === rolesTarget.id ? { ...p, roles: selectedRoles } : p));
      setRolesDialog(false);
    } catch (err) {
      logError('People', err.message, err.stack);
    }
    setRolesSaving(false);
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const openCalc = async (coach, e) => {
    e.stopPropagation();
    setCalcCoach(coach);
    const [headSnap, assistSnap] = await Promise.all([
      getDocs(query(collection(db, 'classes'), where('headCoachId', '==', coach.id))),
      getDocs(query(collection(db, 'classes'), where('assistantCoachIds', 'array-contains', coach.id))),
    ]);
    const headClasses = headSnap.docs.map(d => d.data()).filter(c => c.status !== 'canceled');
    const assistClasses = assistSnap.docs.map(d => d.data()).filter(c => c.status !== 'canceled');
    setCalcSessions({
      head: sessionCount(headClasses), assistant: sessionCount(assistClasses),
      headHours: Math.round(coachHours(headClasses) * 100) / 100,
      assistantHours: Math.round(coachHours(assistClasses) * 100) / 100,
    });
    setCalcDialog(true);
  };

  const applyCalc = async () => {
    setCalcSaving(true);
    const headRate = parseFloat(calcCoach.headRatePerHour) || 0;
    const assistRate = parseFloat(calcCoach.assistantRatePerHour) || 0;
    const totalOwed = Math.round(((calcSessions.headHours * headRate) + (calcSessions.assistantHours * assistRate)) * 100) / 100;
    await updateDoc(doc(db, 'users', calcCoach.id), { totalOwed, totalSessionsLed: calcSessions.head, totalAssistSessions: calcSessions.assistant });
    setAllPeople(prev => prev.map(p => p.id === calcCoach.id ? { ...p, totalOwed, totalSessionsLed: calcSessions.head, totalAssistSessions: calcSessions.assistant } : p));
    setCalcDialog(false);
    setCalcSaving(false);
  };

  const openPay = (coach, e) => {
    e.stopPropagation();
    const bal = Math.max(0, (coach.totalOwed || 0) - (coach.totalPaid || 0));
    setPayCoach(coach);
    setPayForm({ amount: bal.toFixed(2), method: 'Venmo', note: '' });
    setPayDialog(true);
  };

  const loadCoachDetail = async (coach) => {
    setCoachDetailClasses([]);
    setCoachDetailPayments([]);
    setCoachWorkEntries([]);
    setCoachClassesSincePayment(0);
    setCoachClassCounts({ head: 0, assistant: 0, private: 0 });
    setCoachPaidThisMonth(0);
    setCoachPaidYTD(0);
    setCoachDetailLoading(true);
    try {
      const [headSnap, assistSnap, paySnap, workSnap] = await Promise.all([
        getDocs(query(collection(db, 'classes'), where('headCoachId', '==', coach.id))),
        getDocs(query(collection(db, 'classes'), where('assistantCoachIds', 'array-contains', coach.id))),
        getDocs(query(collection(db, 'coachPayments'), where('coachId', '==', coach.id))),
        getDocs(query(collection(db, 'workEntries'), where('workerId', '==', coach.id))),
      ]);
      const workEntries = workSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      const unpaidWorkTotal = workEntries.filter(w => !w.paid).reduce((sm, w) => sm + (w.amount || 0), 0);
      const today = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })();
      const headClasses = headSnap.docs.map(d => ({ id: d.id, role: 'Head Coach', ...d.data() }))
        .filter(c => c.status !== 'canceled' && c.date <= today);
      const assistClasses = assistSnap.docs.map(d => ({ id: d.id, role: 'Assistant', ...d.data() }))
        .filter(c => c.status !== 'canceled' && c.date <= today);
      const allClasses = [...headClasses, ...assistClasses]
        .sort((a, b) => b.date.localeCompare(a.date));
      const payments = paySnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.paidAt?.seconds || 0) - (a.paidAt?.seconds || 0));

      const lastPayment = payments[0];
      const lastPaidDate = lastPayment
        ? (lastPayment.datePaid || (lastPayment.paidAt?.seconds
            ? dateStr(new Date(lastPayment.paidAt.seconds * 1000))
            : ''))
        : '';
      const classesUnpaid = allClasses.filter(c => c.date > lastPaidDate);

      // Per session, not per document — a class covering two courts is stored
      // as two documents but is one coaching session. See utils/coachHours.js.
      const calcHours = coachHours;

      const headRate = parseFloat(coach.headRatePerHour) || 0;
      const asstRate = parseFloat(coach.assistantRatePerHour) || headRate;
      const unpaidHead    = classesUnpaid.filter(c => c.role === 'Head Coach' && c.sessionType !== 'junior-private');
      const unpaidAsst    = classesUnpaid.filter(c => c.role === 'Assistant'  && c.sessionType !== 'junior-private');
      const unpaidPrivate = classesUnpaid.filter(c => c.sessionType === 'junior-private');
      const sincePayment =
        calcHours(unpaidHead)    * headRate +
        calcHours(unpaidAsst)    * asstRate +
        calcHours(unpaidPrivate) * headRate +
        unpaidWorkTotal;

      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear  = now.getFullYear();
      const paidThisMonth = payments
        .filter(p => { const d = p.paidAt?.seconds ? new Date(p.paidAt.seconds * 1000) : null; return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear; })
        .reduce((s, p) => s + (p.amount || 0), 0);
      const paidYTD = payments
        .filter(p => { const d = p.paidAt?.seconds ? new Date(p.paidAt.seconds * 1000) : null; return d && d.getFullYear() === thisYear; })
        .reduce((s, p) => s + (p.amount || 0), 0);

      setCoachClassCounts({
        head: Math.round(calcHours(headClasses.filter(c => c.sessionType !== 'junior-private')) * 100) / 100,
        assistant: Math.round(calcHours(assistClasses.filter(c => c.sessionType !== 'junior-private')) * 100) / 100,
        private: Math.round(calcHours(allClasses.filter(c => c.sessionType === 'junior-private')) * 100) / 100,
      });
      setCoachDetailClasses(allClasses);
      setCoachDetailPayments(payments);
      setCoachWorkEntries(workEntries);
      setCoachClassesSincePayment(sincePayment);
      setCoachPaidThisMonth(paidThisMonth);
      setCoachPaidYTD(paidYTD);
      // Build set of classIds that have already been covered by a payment
      const paidIds = new Set();
      for (const p of payments) {
        (p.classIds || []).forEach(id => paidIds.add(id));
      }
      setPaidClassIds(paidIds);
      // Attendee counts for the pay list — 'in' chunks of 30 rather than one
      // query per class. Reserved and wait-listed rows are intentions, not
      // attendance; legacy rows with no status predate the field and count.
      try {
        const ids = allClasses.map(c => c.id);
        const counts = {};
        for (let i = 0; i < ids.length; i += 30) {
          const attSnap = await getDocs(query(
            collection(db, 'attendance'), where('classId', 'in', ids.slice(i, i + 30))));
          attSnap.docs.forEach(dd => {
            const r = dd.data();
            if (r.status === 'reserved' || r.status === 'waitlisted') return;
            counts[r.classId] = (counts[r.classId] || 0) + 1;
          });
        }
        setCoachClassAttendees(counts);
      } catch (e) { console.warn('Coach attendee counts failed:', e); }
      // Auto-select all unpaid classes so the default amount matches "total owed"
      const autoSelected = {};
      for (const c of allClasses) {
        if (!paidIds.has(c.id)) autoSelected[c.id] = true;
      }
      setSelectedClassIds(autoSelected);
      // Auto-select unpaid work entries too, so the default pay amount covers
      // classes + tournament/other work together.
      const autoWork = {};
      for (const w of workEntries) { if (!w.paid) autoWork[w.id] = true; }
      setSelectedWorkIds(autoWork);
    } catch (err) {
      logError('People', err.message, err.stack);
    }
    setCoachDetailLoading(false);
  };

  // Load athlete session history
  const loadAthleteSessionHistory = async (person) => {
    setAthleteSessionHistory([]);
    setAthleteDetailLoading(true);
    try {
      // For merged user rows (dedup'd adults), person.id is the user UID but
      // attendance records are keyed by the athlete doc id. Query both so we
      // catch records written under either form historically.
      const queryIds = [person.id];
      if (person._athleteId && person._athleteId !== person.id) {
        queryIds.push(person._athleteId);
      }

      const attendanceSnaps = await Promise.all(
        queryIds.map(qid =>
          getDocs(query(collection(db, 'attendance'), where('athleteId', '==', qid)))
        )
      );

      const seen = new Set();
      const attendanceRecords = [];
      attendanceSnaps.forEach(snap => {
        snap.docs.forEach(d => {
          if (seen.has(d.id)) return;
          seen.add(d.id);
          attendanceRecords.push({
            id: d.id,
            ...d.data(),
            checkedInAt: d.data().checkedInAt?.toDate?.() || d.data().checkedInAt,
          });
        });
      });

      // Get all class IDs from attendance records
      const classIds = [...new Set(attendanceRecords.map(a => a.classId))];
      
      if (classIds.length === 0) {
        setAthleteSessionHistory([]);
        return;
      }

      // Fetch class details for each attended class
      const classPromises = classIds.map(classId => 
        getDoc(doc(db, 'classes', classId))
      );
      
      const classSnapshots = await Promise.all(classPromises);
      const classDetails = {};
      
      classSnapshots.forEach(snap => {
        if (snap.exists()) {
          classDetails[snap.id] = { id: snap.id, ...snap.data() };
        }
      });

      // Combine attendance with class details. Keep records even if the
      // referenced class doc has been deleted — render falls back to the
      // attendance doc's own date/sessionType fields so admins still see
      // the full history (and the "Total Sessions" count is accurate).
      const sessionHistory = attendanceRecords
        .map(attendance => ({
          ...attendance,
          classDetails: classDetails[attendance.classId] || null
        }))
        // A canceled class never happened, so it isn't a session attended and
        // shouldn't count toward Total Sessions. Records whose class doc is
        // gone are kept — we can't tell, and dropping them would hide history.
        .filter(r => (r.classDetails?.status || '').toLowerCase() !== 'canceled')
        .sort((a, b) => {
          // Sort by date descending, then by checkedInAt descending.
          // Fall back to attendance.date when class details are missing.
          const dateA = a.classDetails?.date || a.date || '';
          const dateB = b.classDetails?.date || b.date || '';
          if (dateA !== dateB) {
            return dateB.localeCompare(dateA);
          }
          const timeA = a.checkedInAt ? (a.checkedInAt instanceof Date ? a.checkedInAt.getTime() : new Date(a.checkedInAt).getTime()) : 0;
          const timeB = b.checkedInAt ? (b.checkedInAt instanceof Date ? b.checkedInAt.getTime() : new Date(b.checkedInAt).getTime()) : 0;
          return timeB - timeA;
        });

      setAthleteSessionHistory(sessionHistory);
    } catch (err) {
      logError('People', err, { function: 'loadAthleteSessionHistory', personId: person.id });
      setAthleteSessionHistory([]);
    } finally {
      setAthleteDetailLoading(false);
    }
  };

  const openAthleteDetail = async (person, e) => {
    e.stopPropagation();
    setAthleteDetailPerson(person);
    setAthleteDetailDialog(true);
    await loadAthleteSessionHistory(person);
  };

  const openMemberDetail = (person, e) => {
    e?.stopPropagation?.();
    const targetUserId = person._collection === 'users' ? person.id : person._memberUserId;
    setMemberDetail({ ...person, _targetUserId: targetUserId });
    setDayPassCredits([]); setCreditReason('');
    getDocs(query(collection(db, 'dayPassCredits'), where('userId', '==', targetUserId)))
      .then(snap => setDayPassCredits(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => { /* collection may not exist yet */ });
    setMemberMsg(null);
    setMemberFreezeMonths(1);
    setMemberDetailDialog(true);
  };

  // Admin membership action via manageMembership({targetUserId}); refreshes
  // the user doc into both the dialog and the people list afterward.
  const reloadCredits = async (uid) => {
    try {
      const snap = await getDocs(query(collection(db, 'dayPassCredits'), where('userId', '==', uid)));
      setDayPassCredits(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { /* non-fatal */ }
  };

  const issueCredit = async () => {
    if (!memberDetail) return;
    setCreditBusy(true); setMemberMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'issueDayPassCredit');
      await fn({ userId: memberDetail._targetUserId, reason: creditReason });
      setCreditReason('');
      await reloadCredits(memberDetail._targetUserId);
      setMemberMsg({ severity: 'success', text: 'Day pass credit issued. They can redeem it whenever they like.' });
    } catch (e) {
      setMemberMsg({ severity: 'error', text: e.message || 'Could not issue credit.' });
    } finally { setCreditBusy(false); }
  };

  const creditAction = async (fnName, creditId, okText) => {
    setCreditBusy(true); setMemberMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), fnName);
      const { data } = await fn({ creditId });
      await reloadCredits(memberDetail._targetUserId);
      // Redemption rewrites the membership map — refresh it so the dialog isn't stale.
      const snap = await getDoc(doc(db, 'users', memberDetail._targetUserId));
      if (snap.exists()) setMemberDetail(prev => ({ ...prev, membership: snap.data().membership }));
      // A redeemed pass comes with a gate PIN; the admin may be the one
      // reading it out to somebody at the counter.
      const pinText = data?.pin
        ? ` Gate PIN ${data.pin}${data.pinEmailed ? ' (also emailed to them)' : ''}.` : '';
      setMemberMsg({ severity: 'success', text: okText + pinText });
    } catch (e) {
      setMemberMsg({ severity: 'error', text: e.message || 'Action failed.' });
    } finally { setCreditBusy(false); }
  };

  const memberAction = async (action, months) => {
    if (!memberDetail) return;
    setMemberBusy(true); setMemberMsg(null);
    try {
      const fns = getFunctions(app, 'us-central1');
      const fn = httpsCallable(fns, 'manageMembership');
      await fn({ action, months, targetUserId: memberDetail._targetUserId });
      const snap = await getDoc(doc(db, 'users', memberDetail._targetUserId));
      if (snap.exists()) {
        const fresh = { ...memberDetail, membership: snap.data().membership };
        setMemberDetail(fresh);
        setAllPeople(prev => prev.map(p =>
          ((p._collection === 'users' && p.id === memberDetail._targetUserId) || p._memberUserId === memberDetail._targetUserId)
            ? { ...p, membership: snap.data().membership } : p));
      }
      setMemberMsg({ severity: 'success', text: `${action} complete.` });
    } catch (err) {
      setMemberMsg({ severity: 'error', text: err.message || 'Action failed.' });
    } finally { setMemberBusy(false); }
  };

  const openContractorDetail = async (person, e) => {
    if (e) e.stopPropagation();
    setContractorPerson(person);
    setContractorRate(person.hourlyRate != null ? String(person.hourlyRate) : '');
    setContractorPayType(person.payType || 'per_job');
    setContractorEntries([]);
    setContractorPayments([]);
    setSelectedEntryIds({});
    setContractorDialog(true);
    setContractorLoading(true);
    try {
      const [snap, paySnap] = await Promise.all([
        getDocs(query(collection(db, 'workEntries'), where('workerId', '==', person.id))),
        getDocs(query(collection(db, 'coachPayments'), where('workerId', '==', person.id))),
      ]);
      const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setContractorEntries(entries);
      const pays = paySnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.paidAt?.seconds || 0) - (a.paidAt?.seconds || 0));
      setContractorPayments(pays);
    } catch (err) { console.error('load work entries failed:', err); }
    setContractorLoading(false);
  };

  const saveContractorRate = async () => {
    if (!contractorPerson) return;
    const r = parseFloat(contractorRate);
    if (isNaN(r) || r < 0) return;
    setRateSaving(true);
    try {
      await updateDoc(doc(db, 'users', contractorPerson.id), { hourlyRate: r, payType: contractorPayType });
      setContractorPerson(p => ({ ...p, hourlyRate: r, payType: contractorPayType }));
      setAllPeople(prev => prev.map(p => p.id === contractorPerson.id ? { ...p, hourlyRate: r, payType: contractorPayType } : p));
    } catch (err) { alert('Rate save failed: ' + err.message); }
    setRateSaving(false);
  };

  const openLogWork = () => {
    setWorkEditId(null);
    setWorkForm({
      type: 'hours', hours: '', rate: contractorPerson?.hourlyRate != null ? String(contractorPerson.hourlyRate) : '',
      amount: '', description: '', date: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
    });
    setLogWorkOpen(true);
  };

  const openEditWork = (en) => {
    setWorkEditId(en.id);
    setWorkForm({
      type: en.type || (en.hours ? 'hours' : 'task'),
      hours: en.hours != null ? String(en.hours) : '',
      rate: en.rate != null ? String(en.rate) : (contractorPerson?.hourlyRate != null ? String(contractorPerson.hourlyRate) : ''),
      amount: en.amount != null ? String(en.amount) : '',
      description: en.description || '', date: en.date || (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
    });
    setLogWorkOpen(true);
  };

  const deleteWorkEntry = async (en) => {
    if (!window.confirm(`Delete this ${en.paid ? 'PAID ' : ''}work entry ($${(en.amount || 0).toFixed(2)})?`)) return;
    try {
      await deleteDoc(doc(db, 'workEntries', en.id));
      setContractorEntries(prev => prev.filter(x => x.id !== en.id));
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const saveWorkEntry = async () => {
    if (!contractorPerson) return;
    const f = workForm;
    let amount = 0, hours = null, rate = null;
    if (f.type === 'hours') {
      hours = parseFloat(f.hours); rate = parseFloat(f.rate);
      if (isNaN(hours) || hours <= 0 || isNaN(rate) || rate < 0) { alert('Enter valid hours and rate.'); return; }
      amount = Math.round(hours * rate * 100) / 100;
    } else {
      amount = parseFloat(f.amount);
      if (isNaN(amount) || amount <= 0) { alert('Enter a valid amount.'); return; }
    }
    if (!f.description.trim()) { alert('Add a short description.'); return; }
    setLogWorkSaving(true);
    try {
      const entry = {
        workerId: contractorPerson.id,
        workerName: `${contractorPerson.firstName || ''} ${contractorPerson.lastName || ''}`.trim(),
        type: f.type,
        hours: f.type === 'hours' ? hours : null,
        rate: f.type === 'hours' ? rate : null,
        amount,
        description: f.description.trim(),
        date: f.date,
        paid: false,
        paidAt: null,
        createdBy: auth.currentUser ? auth.currentUser.uid : null,
        createdAt: serverTimestamp(),
      };
      if (workEditId) {
        const upd = { type: entry.type, hours: entry.hours, rate: entry.rate,
          amount: entry.amount, description: entry.description, date: entry.date, approved: true };
        await updateDoc(doc(db, 'workEntries', workEditId), upd);
        setContractorEntries(prev => prev.map(x => x.id === workEditId ? { ...x, ...upd } : x));
      } else {
        const ref = await addDoc(collection(db, 'workEntries'), { ...entry, approved: true });
        setContractorEntries(prev => [{ id: ref.id, ...entry, approved: true }, ...prev]);
      }
      setWorkEditId(null);
      setLogWorkOpen(false);
    } catch (err) { alert('Save failed: ' + err.message); }
    setLogWorkSaving(false);
  };

  const contractorUnpaidTotal = contractorEntries
    .filter(e => !e.paid && e.approved !== false)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  // The day a payment was made: the date entered when recording it, falling
  // back to when it was recorded (payments from before the date field).
  const contractorPayDay = (pmt) => pmt.datePaid
    || (pmt.paidAt?.seconds ? dateStr(new Date(pmt.paidAt.seconds * 1000)) : '');
  const _cpToday = localToday();
  const contractorPaidThisMonth = contractorPayments
    .filter(pmt => contractorPayDay(pmt).slice(0, 7) === _cpToday.slice(0, 7))
    .reduce((t, pmt) => t + (pmt.amount || 0), 0);
  const contractorPaidYTD = contractorPayments
    .filter(pmt => contractorPayDay(pmt).slice(0, 4) === _cpToday.slice(0, 4))
    .reduce((t, pmt) => t + (pmt.amount || 0), 0);

  const selectedEntries = contractorEntries.filter(e => selectedEntryIds[e.id] && !e.paid);
  const selectedTotal = selectedEntries.reduce((sum, e) => sum + (e.amount || 0), 0);

  const payContractor = async () => {
    if (!contractorPerson || selectedEntries.length === 0 || !payWorkForm.datePaid) return;
    setPayWorkSaving(true);
    try {
      const entryIds = selectedEntries.map(e => e.id);
      // datePaid is the day the money went out, as entered; paidAt stays the
      // moment it was recorded — the same split coach payments use.
      const { datePaid } = payWorkForm;
      await addDoc(collection(db, 'coachPayments'), {
        workerId: contractorPerson.id,
        coachId: contractorPerson.id,
        coachName: `${contractorPerson.firstName || ''} ${contractorPerson.lastName || ''}`.trim(),
        amount: Math.round(selectedTotal * 100) / 100,
        method: payWorkForm.method,
        note: payWorkForm.note,
        datePaid,
        entryIds,
        type: 'contractor',
        paidAt: serverTimestamp(),
      });
      const batch = writeBatch(db);
      entryIds.forEach(id => batch.update(doc(db, 'workEntries', id), { paid: true, datePaid, paidAt: serverTimestamp() }));
      await batch.commit();
      setContractorEntries(prev => prev.map(e => entryIds.includes(e.id) ? { ...e, paid: true, datePaid } : e));
      setContractorPayments(prev => [{
        id: 'local-' + Date.now(),
        amount: Math.round(selectedTotal * 100) / 100,
        method: payWorkForm.method,
        note: payWorkForm.note,
        datePaid,
        paidAt: { seconds: Math.floor(Date.now() / 1000) },
      }, ...prev]);
      setSelectedEntryIds({});
      setPayWorkOpen(false);
      setAllPeople(prev => prev.map(p => p.id === contractorPerson.id
        ? { ...p, _contractorOutstanding: Math.max(0, (p._contractorOutstanding || 0) - selectedTotal) }
        : p));
    } catch (err) { alert('Payment failed: ' + err.message); }
    setPayWorkSaving(false);
  };

  const openCoachDetail = async (coach, e) => {
    e.stopPropagation();
    setCoachDetailPerson(coach);
    setDetailPayForm({ amount: '', method: 'Venmo', datePaid: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(), note: '' });
    setDetailPaySuccess('');
    setCoachDetailDialog(true);
    await loadCoachDetail(coach);
  };

  const handlePay = async () => {
    const amount = parseFloat(payForm.amount);
    if (!amount || amount <= 0) return;
    setPaySaving(true);
    const newTotalPaid = (payCoach.totalPaid || 0) + amount;
    await updateDoc(doc(db, 'users', payCoach.id), { totalPaid: newTotalPaid, lastPaidAt: serverTimestamp(), lastPaymentMethod: payForm.method });
    await addDoc(collection(db, 'coachPayments'), { coachId: payCoach.id, coachName: `${payCoach.firstName} ${payCoach.lastName}`, amount, method: payForm.method, note: payForm.note, paidAt: serverTimestamp() });
    setAllPeople(prev => prev.map(p => p.id === payCoach.id ? { ...p, totalPaid: newTotalPaid } : p));
    setPayDialog(false);
    setPaySaving(false);
    setPaySuccess(`Payment of $${amount.toFixed(2)} recorded for ${payCoach.firstName} ${payCoach.lastName}.`);
    setTimeout(() => setPaySuccess(''), 4000);
  };

  // ── compute amount owed for currently selected classes ──
  const selectedClassesTotal = (() => {
    const coach = coachDetailPerson;
    if (!coach) return 0;
    const headRate = parseFloat(coach.headRatePerHour) || 0;
    const asstRate = parseFloat(coach.assistantRatePerHour) || headRate;
    const hoursOf = (c) => {
      if (!c.startTime || !c.endTime) return 1;
      const [sh, sm] = c.startTime.split(':').map(Number);
      const [eh, em] = c.endTime.split(':').map(Number);
      return ((eh * 60 + em) - (sh * 60 + sm)) / 60;
    };
    let total = 0;
    for (const c of coachDetailClasses) {
      if (!selectedClassIds[c.id]) continue;
      const h = hoursOf(c);
      if (c.sessionType === 'junior-private') total += h * headRate;
      else if (c.role === 'Head Coach') total += h * headRate;
      else if (c.role === 'Assistant') total += h * asstRate;
    }
    return Math.round(total * 100) / 100;
  })();

  // Total of currently-selected work entries.
  const selectedWorkTotal = coachWorkEntries
    .filter(w => selectedWorkIds[w.id] && !w.paid)
    .reduce((sum, w) => sum + (w.amount || 0), 0);
  const selectedPayTotal = Math.round((selectedClassesTotal + selectedWorkTotal) * 100) / 100;

  // Autofill the amount when selection changes (classes + work)
  useEffect(() => {
    if (!coachDetailDialog) return;
    setDetailPayForm(f => ({ ...f, amount: selectedPayTotal ? String(selectedPayTotal) : '' }));
  }, [selectedPayTotal, coachDetailDialog]);

  const handleDetailPay = async () => {
    const amount = parseFloat(detailPayForm.amount);
    if (!amount || amount <= 0) return;
    // Guard: paying while unpaid work entries exist but none are selected
    // leaves those entries feeding Outstanding forever (silent mismatch).
    const uncoveredWork = coachWorkEntries.filter(w => !w.paid && !selectedWorkIds[w.id]);
    if (uncoveredWork.length) {
      const uncoveredTotal = uncoveredWork.reduce((s, w) => s + (w.amount || 0), 0);
      if (!window.confirm(
        `${uncoveredWork.length} unpaid work entr${uncoveredWork.length === 1 ? 'y' : 'ies'} ($${uncoveredTotal.toFixed(2)}) ` +
        `will NOT be marked paid by this payment. If this payment covers that work, cancel and check the entries first. Record anyway?`
      )) return;
    }
    setDetailPaySaving(true);
    try {
      const coach = coachDetailPerson;
      const newTotalPaid = (coach.totalPaid || 0) + amount;
      // Collect selected classes (IDs and dates) for audit trail
      const selectedClasses = coachDetailClasses.filter(c => selectedClassIds[c.id]);
      const classIds = selectedClasses.map(c => c.id);
      const classDates = [...new Set(selectedClasses.map(c => c.date))].sort();
      // Work entries covered by this payment (tournament/other work).
      const selectedWork = coachWorkEntries.filter(w => selectedWorkIds[w.id] && !w.paid);
      const workEntryIds = selectedWork.map(w => w.id);
      const payRecord = {
        coachId: coach.id,
        coachName: `${coach.firstName} ${coach.lastName}`,
        amount,
        method: detailPayForm.method,
        note: detailPayForm.note,
        datePaid: detailPayForm.datePaid,
        classIds,
        classDates,
        classCount: classIds.length,
        workEntryIds,
        paidAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'users', coach.id), { totalPaid: newTotalPaid, lastPaidAt: serverTimestamp(), lastPaymentMethod: detailPayForm.method });
      const ref = await addDoc(collection(db, 'coachPayments'), payRecord);
      // Update local state
      setAllPeople(prev => prev.map(p => p.id === coach.id ? { ...p, totalPaid: newTotalPaid } : p));
      setCoachDetailPerson(prev => ({ ...prev, totalPaid: newTotalPaid }));
      setCoachDetailPayments(prev => [{ id: ref.id, ...payRecord, paidAt: { seconds: Date.now() / 1000 } }, ...prev]);
      // Settle covered work entries: mark them paid in Firestore + locally.
      if (workEntryIds.length) {
        const wb = writeBatch(db);
        workEntryIds.forEach(id => wb.update(doc(db, 'workEntries', id), { paid: true, paidAt: serverTimestamp() }));
        await wb.commit();
        setCoachWorkEntries(prev => prev.map(w => workEntryIds.includes(w.id) ? { ...w, paid: true } : w));
      }
      // Mark newly-paid class IDs as paid locally
      setPaidClassIds(prev => { const next = new Set(prev); classIds.forEach(id => next.add(id)); return next; });
      // Clear selection
      setSelectedClassIds({});
      setSelectedWorkIds({});
      setDetailPayForm({ amount: '', method: 'Venmo', datePaid: '', note: '' });
      setDetailPaySuccess(`$${amount.toFixed(2)} recorded${classIds.length ? ` for ${classIds.length} class${classIds.length !== 1 ? 'es' : ''}` : ''}.`);
      setTimeout(() => setDetailPaySuccess(''), 4000);
      await loadCoachDetail({ ...coach, totalPaid: newTotalPaid });
    } catch (err) {
      logError('People', err.message, err.stack);
    }
    setDetailPaySaving(false);
  };

  const handlePromoteToActive = async (person, e) => {
    e.stopPropagation();
    try {
      if (person._collection === 'athletes') {
        await updateDoc(doc(db, 'athletes', person.id), { isPromotedToActive: true });
      } else {
        const newRoles = (person.roles || []).filter(r => r !== 'lead');
        await updateDoc(doc(db, 'users', person.id), { roles: newRoles, isPromotedToActive: true });
      }
      setAllPeople(prev => prev.map(p => p.id === person.id ? { ...p, isPromotedToActive: true } : p));
      setPaySuccess(`${person.firstName} ${person.lastName} promoted to Active.`);
      setTimeout(() => setPaySuccess(''), 4000);
    } catch (err) {
      console.error('Promote failed:', err);
      logError('People', err.message || String(err), err.stack || '');
    }
  };

  const handleArchive = async () => {
    if (!selectedPerson) return;
    setArchiving(true);
    try {
      await updateDoc(doc(db, selectedPerson._collection, selectedPerson.id), { isArchived: true });
      setAllPeople(prev => prev.map(p => p.id === selectedPerson.id ? { ...p, isArchived: true } : p));
      setArchiveDialog(false);
      setPaySuccess(`${selectedPerson.firstName} ${selectedPerson.lastName} archived.`);
      setTimeout(() => setPaySuccess(''), 4000);
    } catch (err) { console.error('Archive failed:', err); }
    setArchiving(false);
    setRowAnchor(null);
  };

  const handleUnarchive = async (person, e) => {
    e.stopPropagation();
    const updates = { isArchived: false };
    // Defensive cleanup: if athleteType was set to a non-standard value like "archived",
    // infer the correct type. Use birthday first (under 18 = junior), then fall back to
    // parentUserId presence as a hint (parent → junior, otherwise adult).
    if (person._collection === 'athletes') {
      const t = (person.athleteType || '').toLowerCase();
      if (t !== 'junior' && t !== 'adult') {
        let inferredType = 'adult';
        if (person.birthday) {
          const bd = new Date(person.birthday);
          if (!isNaN(bd)) {
            const today = new Date();
            let age = today.getFullYear() - bd.getFullYear();
            const m = today.getMonth() - bd.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
            inferredType = age < 18 ? 'junior' : 'adult';
          }
        } else if (person.parentUserId) {
          inferredType = 'junior';
        }
        updates.athleteType = inferredType;
      }
    }
    await updateDoc(doc(db, person._collection, person.id), updates);
    setAllPeople(prev => prev.map(p => p.id === person.id ? { ...p, ...updates } : p));
    setPaySuccess(`${person.firstName} ${person.lastName} restored.`);
    setTimeout(() => setPaySuccess(''), 4000);
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return null;
    return sortDir === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
      : <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />;
  };

  const tabPeople = getTabPeople();

  const filtered = tabPeople
    .filter(p => {
      if (!search) return true;
      const q = search.toLowerCase();
      // Raw fields (name, email, phone...) plus the values actually shown in
      // the Type, Status and Plan columns — those are derived, so a plain
      // field scan would never match "expired", "day pass" or a plan name.
      const derived = [
        getMemberType(p),
        getStatus(p),
        p.plan, p.planName,
        p.membership?.tierName,
      ];
      return Object.values(p).some(v => v && typeof v === 'string' && v.toLowerCase().includes(q))
        || derived.some(v => v && String(v).toLowerCase().includes(q));
    })
    .filter(p => {
      if (!filter) return true;
      return getMemberType(p) === filter || getStatus(p) === filter;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortCol === 'name') { av = (a.firstName || '').toLowerCase(); bv = (b.firstName || '').toLowerCase(); }
      else if (sortCol === 'memberType') { av = getMemberType(a); bv = getMemberType(b); }
      else if (sortCol === 'status') { av = getStatus(a); bv = getStatus(b); }
      else if (sortCol === 'sessions') { av = a.sessionBalance ?? -1; bv = b.sessionBalance ?? -1; }
      else if (sortCol === 'lastCheckIn') { av = a.lastCheckIn?.seconds ?? 0; bv = b.lastCheckIn?.seconds ?? 0; }
      else if (sortCol === 'plan') { av = a.plan || a.planName || ''; bv = b.plan || b.planName || ''; }
      else { av = a[sortCol] || ''; bv = b[sortCol] || ''; }
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const handleRowMenu = (e, person) => {
    e.stopPropagation();
    setSelectedPerson(person);
    setRowAnchor(e.currentTarget);
  };

  const viewProfileFor = (person) => {
    // A member manager's whole remit is the membership, so a row click opens
    // the Membership Details dialog rather than a profile page. The dialog's
    // controls are already gated on canEditPeople, so it reads without acting.
    // Anything without a membership opens nothing — their tab only lists
    // members, and a profile page is more than this role is meant to see.
    if (isMemberManager) {
      if (isMemberRow(person)) openMemberDetail(person);
      return;
    }
    // Parents and All tabs always route to UserProfile (/people/{id}) so multi-role users
    // (e.g. coach-parents) see their full context including linked athletes.
    // Coaches tab specifically shows coach detail. Other tabs route by collection/role.
    const tabLabel = TABS[tab];
    const forceUserProfile = tabLabel === 'Parents' || tabLabel === 'All';
    // A contractor's row opens Contractor Details — their work and pay are what
    // an admin comes to them for. Coaches keep their own page, and the Parents
    // tab still means the parent profile. ⋮ → View Profile reaches the page.
    const roles = person.roles || [];
    if (person._collection === 'users' && roles.includes('contractor')
        && !roles.some(r => ['coach', 'assistant_coach'].includes(r)) && tabLabel !== 'Parents') {
      openContractorDetail(person);
      return;
    }
    if (person._collection === 'athletes') {
      navigate(`/athletes/${person.id}`);
    } else if (!forceUserProfile && person._collection === 'users' && (person.roles||[]).some(r=>['coach','assistant_coach'].includes(r))) {
      navigate(`/people/${person.id}`);
    } else if (!forceUserProfile && person._collection === 'users' && person._athleteId) {
      navigate(`/athletes/${person._athleteId}`);
    } else {
      navigate(`/people/${person.id}`);
    }
  };

  const handleView = () => {
    setRowAnchor(null);
    viewProfileFor(selectedPerson);
  };

  // A row click goes straight to the profile, for every kind of account —
  // juniors, adults, coaches and contractors included. The detail dialogs
  // (session history, membership, coach and contractor pay) are one step away
  // on the row's ⋮ menu. Juniors used to open Session History on click, which
  // is not where anyone looking someone up expects to land.
  const isMemberRow = (person) => Boolean(
    person.membership?.tierId && (person._collection === 'users' || person._memberUserId)
  );

  const goesStraightToProfile = (person) => Boolean(person);

  const handleEdit = () => {
    setRowAnchor(null);
    const tabLabel = TABS[tab];
    const forceUserProfile = tabLabel === 'Parents' || tabLabel === 'All';
    if (selectedPerson._collection === 'athletes') {
      navigate(`/athletes/${selectedPerson.id}?edit=true`);
    } else if (!forceUserProfile && selectedPerson._collection === 'users' && (selectedPerson.roles||[]).some(r=>['coach','assistant_coach'].includes(r))) {
      navigate(`/people/${selectedPerson.id}?edit=true`);
    } else if (!forceUserProfile && selectedPerson._collection === 'users' && selectedPerson._athleteId) {
      navigate(`/athletes/${selectedPerson._athleteId}?edit=true`);
    } else {
      navigate(`/people/${selectedPerson.id}?edit=true`);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, selectedPerson._collection, selectedPerson.id));
      setAllPeople(prev => prev.filter(p => p.id !== selectedPerson.id));
      setDeleteDialog(false);
    } catch (err) {
      logError('People', err.message || String(err), err.stack || '');
      setError('Failed to delete. Please try again.');
    }
    setDeleting(false);
  };

  // ── Duplicate finder ────────────────────────────────────────────────────────
  const handleFindDuplicates = async () => {
    setDupsLoading(true);
    setDupsMsg('');
    setDupsDialog(true);
    setDupsKeeper({});
    try {
      const snap = await getDocs(collection(db, 'athletes'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const dupGroupsRaw = groupDuplicateAthletes(all);
      // Enrich each doc with attendance count
      const enrichedGroups = await Promise.all(
        dupGroupsRaw.map(async ({ key, kind, reason, docs }) => {
          const withCounts = await Promise.all(docs.map(async d => {
            try {
              const attSnap = await getDocs(query(collection(db, 'attendance'), where('athleteId', '==', d.id)));
              return { ...d, _attendanceCount: attSnap.size };
            } catch { return { ...d, _attendanceCount: 0 }; }
          }));
          return { key, kind, reason, docs: withCounts };
        })
      );
      // Default keeper: prefer doc with most attendance, then not archived, then highest sessionBalance
      const keeperMap = {};
      for (const g of enrichedGroups) {
        const sorted = [...g.docs].sort((a, b) => {
          if (b._attendanceCount !== a._attendanceCount) return b._attendanceCount - a._attendanceCount;
          if ((a.isArchived ? 1 : 0) !== (b.isArchived ? 1 : 0)) return (a.isArchived ? 1 : 0) - (b.isArchived ? 1 : 0);
          return (b.sessionBalance || 0) - (a.sessionBalance || 0);
        });
        keeperMap[g.key] = sorted[0].id;
      }
      setDupsGroups(enrichedGroups);
      setDupsKeeper(keeperMap);
      if (enrichedGroups.length === 0) setDupsMsg('No duplicates found.');
    } catch (err) {
      logError('People', err.message || String(err), err.stack || '');
      setDupsMsg('Failed to scan for duplicates: ' + err.message);
    }
    setDupsLoading(false);
  };

  const handleMergeDuplicate = async (group) => {
    const keeperId = dupsKeeper[group.key];
    if (!keeperId) { setDupsMsg('Pick which one to keep first.'); return; }
    const keeper = group.docs.find(d => d.id === keeperId);
    const toRemove = group.docs.filter(d => d.id !== keeperId);
    const keeperName = `${keeper.firstName || ''} ${keeper.lastName || ''}`.trim() || keeper.email || keeperId;
    if (!window.confirm(
      `Keep "${keeperName}" (${keeper._attendanceCount} attendance records).\n` +
      `Reassign attendance from ${toRemove.length} duplicate${toRemove.length > 1 ? 's' : ''} and delete them.\n\nProceed?`
    )) return;
    setDupsMerging(true);
    setDupsMsg('');
    try {
      // Reassign attendance records of each removed doc to the keeper
      for (const rem of toRemove) {
        const attSnap = await getDocs(query(collection(db, 'attendance'), where('athleteId', '==', rem.id)));
        if (!attSnap.empty) {
          // Batch updates (500 ops per batch max)
          const chunks = [];
          const docs = attSnap.docs;
          for (let i = 0; i < docs.length; i += 450) chunks.push(docs.slice(i, i + 450));
          for (const chunk of chunks) {
            const batch = writeBatch(db);
            chunk.forEach(d => batch.update(d.ref, { athleteId: keeperId }));
            await batch.commit();
          }
        }
        // Delete the duplicate athlete doc
        await deleteDoc(doc(db, 'athletes', rem.id));
      }
      // Refresh local state — remove merged docs
      const removedIds = new Set(toRemove.map(d => d.id));
      setAllPeople(prev => prev.filter(p => !(p._collection === 'athletes' && removedIds.has(p.id))));
      // Remove this group from the list
      setDupsGroups(prev => prev.filter(g => g.key !== group.key));
      logEvent('athlete.merged', {
        target: { id: keeperId, email: keeper.email || '', name: keeperName },
        description: `Merged ${toRemove.length} duplicate athlete record${toRemove.length > 1 ? 's' : ''} into "${keeperName}" (attendance reassigned, duplicates deleted)`,
        metadata: {
          keeperId,
          removed: toRemove.map(d => ({ id: d.id, email: d.email || '', attendanceCount: d._attendanceCount || 0 })),
        },
      });
      setDupsMsg(`Merged: kept "${keeperName}", removed ${toRemove.length} duplicate${toRemove.length > 1 ? 's' : ''}.`);
    } catch (err) {
      logError('People', err.message || String(err), err.stack || '');
      setDupsMsg('Merge failed: ' + err.message);
    }
    setDupsMerging(false);
  };

  const handleAddPerson = (role) => {
    setAddAnchor(null);
    navigate(`/people/new?role=${role}`);
  };

  // Column header for current tab
  const getTypeLabel = () => {
    switch (tab) {
      case 0: return 'Type';
      case 2: return 'Role';
      default: return 'Type';
    }
  };

  const handlePhotoUpload = async (e, person) => {
    const file = e.target.files[0];
    if (!file) return;
    const isAthlete = !!person.parentUserId || person.athleteType;
    const collection_name = isAthlete ? 'athletes' : 'users';
    const id = isAthlete ? person.id : person.userId;
    if (!id) return;
    setUploadingPhotoId(person.id);
    try {
      const storageRef = ref(storage, `profile_photos/${collection_name}/${id}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, collection_name, id), { photoURL: url });
      // Update local state
      setAllPeople(prev => prev.map(p => p.id === person.id ? { ...p, photoURL: url } : p));
    } catch (err) {
      console.error('Photo upload failed:', err);
      logError('People', err.message || String(err), err.stack || '');
    }
    setUploadingPhotoId(null);
  };

  const openCustomerDialog = (cust) => {
    setCustomerEditTarget(cust || null);
    setCustomerDialog(true);
  };

  // Upsert whatever CustomerEditDialog wrote, wherever it was opened from —
  // the Add button, the row kebab, or the Edit button inside CompanyPanel.
  const applyCustomerSave = (saved) => {
    setCustomers(prev => (prev.some(c => c.id === saved.id)
      ? prev.map(c => (c.id === saved.id ? { ...c, ...saved } : c))
      : [...prev, saved]
    ).sort((a, b) => (a.organizationName || '').localeCompare(b.organizationName || '')));
    setCustomerDetailTarget(t => (t && t.id === saved.id ? { ...t, ...saved } : t));
  };

  // A customer with a login is a person like anyone else on this page, so the
  // row goes to their profile — their court time and billing are the Sessions
  // tab there (company details under Contact Info), the same panel this dialog
  // shows. Customers who never signed in (East
  // River, Timber Creek) have no profile to open, so they keep the dialog.
  const openCustomerDetail = (c) => {
    if (!c) return;
    if (c.userId) { navigate(`/people/${c.userId}?tab=sessions`); return; }
    setCustomerDetailTarget(c);
  };

  const handleArchiveCustomer = async (cust, archived) => {
    try {
      await updateDoc(doc(db, 'customers', cust.id), { archived });
      setCustomers(prev => prev.map(c => c.id === cust.id ? { ...c, archived } : c));
    } catch (e) {
      logError('People', 'archive customer: ' + (e.message || String(e)), e.stack || '');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerDeleteTarget) return;
    try {
      await deleteDoc(doc(db, 'customers', customerDeleteTarget.id));
      setCustomers(prev => prev.filter(c => c.id !== customerDeleteTarget.id));
    } catch (e) {
      logError('People', 'delete customer: ' + (e.message || String(e)), e.stack || '');
    }
    setCustomerDeleteTarget(null);
  };

  return (
    <Layout>
      <Box sx={{ p: { xs: 1.5, sm: 3 }, background: '#0D1E35', minHeight: '100%', fontFamily: '"Barlow", sans-serif', overflowX: 'hidden', maxWidth: '100vw', boxSizing: 'border-box' }}>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', letterSpacing: '3px', color: 'white', lineHeight: 1 }}>People</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.35)', mt: 0.5, fontSize: '0.78rem', letterSpacing: '1px' }}>
              {TABS[tab] === 'Customers' ? customers.length : filtered.length} {TABS[tab].toLowerCase()}
            </Typography>
          </Box>
		  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              placeholder="Search people..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{
                width: { xs: '100%', sm: 260 },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#C9A84C' },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(201,168,76,0.6)' }} /></InputAdornment>
              }}
            />
            {isAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<MergeTypeIcon />}
                  onClick={handleFindDuplicates}
                  sx={{
                    color: '#C9A84C', borderColor: 'rgba(201,168,76,0.4)',
                    '&:hover': { borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.08)' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Find Duplicates
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={e => setAddAnchor(e.currentTarget)}
                  sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' }, whiteSpace: 'nowrap' }}
                >
                  Add Person
                </Button>
              </>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Add Person Menu */}
        <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
          {ADD_MENU.map(item => (
            <MenuItem key={item.role} onClick={() => handleAddPerson(item.role)}>{item.label}</MenuItem>
          ))}
          <MenuItem key="customer" onClick={() => {
            setAddAnchor(null);
            const ci = TABS.indexOf('Customers');
            if (ci >= 0) setTab(ci);
            openCustomerDialog(null);
          }}>Customer</MenuItem>
        </Menu>

        {/* Tabs */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', background: '#0D1E35', border: '1px solid rgba(201,168,76,0.15)' }}>
          <Box sx={{ borderBottom: '1px solid rgba(201,168,76,0.15)', backgroundColor: '#0a1829' }}>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 1.5, py: 1.25 }}>
                {TABS.map((label, i) => {
                  const active = tab === i;
                  return (
                    <Chip
                      key={label}
                      label={label === 'Coaches' ? 'Staff' : label}
                      onClick={() => { setTab(i); setSearch(''); }}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        height: 28,
                        backgroundColor: active ? '#C9A84C' : 'rgba(255,255,255,0.06)',
                        color: active ? '#0D1E35' : 'rgba(255,255,255,0.65)',
                        border: active ? 'none' : '1px solid rgba(201,168,76,0.2)',
                        '&:hover': {
                          backgroundColor: active ? '#C9A84C' : 'rgba(201,168,76,0.15)',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            ) : (
              <Tabs
                value={tab}
                onChange={(e, v) => {
                  setTab(v);
                  setSearch('');
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ px: { xs: 1, sm: 2 }, minHeight: 48 }}
                TabIndicatorProps={{ style: { backgroundColor: '#C9A84C' } }}
              >
                {TABS.map(label => (
                  <Tab key={label} label={label === 'Coaches' ? 'Staff' : label} sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.5)', '&.Mui-selected': { color: '#C9A84C' } }} />
                ))}
              </Tabs>
            )}
          </Box>

          {/* Filter bar */}
          {isAdmin && (
            <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 2, justifyContent: 'flex-end', borderBottom: '1px solid rgba(201,168,76,0.1)', backgroundColor: '#0a1829' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter</InputLabel>
                <Select value={filter} label="Filter" onChange={e => setFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem disabled sx={{ fontSize: '0.75rem', color: '#aaa' }}>— Member Type —</MenuItem>
                  <MenuItem value="Member">Member</MenuItem>
                  <MenuItem value="Day Pass">Day Pass</MenuItem>
                  <MenuItem value="Non-Member">Non-Member</MenuItem>
                  <MenuItem disabled sx={{ fontSize: '0.75rem', color: '#aaa' }}>— Status —</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                  <MenuItem value="Past Due">Past Due</MenuItem>
                  <MenuItem value="Frozen">Frozen</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Alert">Alert</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress />
            </Box>
          ) : (filtered.length === 0 && TABS[tab] !== 'Customers') ? (
            <Box sx={{ p: 8, textAlign: 'center', background: '#0D1E35' }}>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                {search ? 'No results matching your search.' : `No ${TABS[tab].toLowerCase()} found.`}
              </Typography>
            </Box>
          ) : (
            <>
            {/* Coach Pay Summary — only on Coaches tab */}
            {TABS[tab] === 'Coaches' && isAdmin && (() => {
              return (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Total Owed', value: `$${coachSummary.totalOwed.toFixed(2)}`, color: '#E67E22' },
                    { label: 'Paid This Month', value: `$${coachSummary.paidThisMonth.toFixed(2)}`, color: '#27AE60' },
                    { label: 'Paid YTD', value: `$${coachSummary.paidYTD.toFixed(2)}`, color: '#7FB3D3' },
                  ].map(({ label, value, color }) => (
                    <Paper key={label} elevation={1} sx={{ p: 1.5, borderRadius: 2, minWidth: 120, textAlign: 'center', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>{label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
                    </Paper>
                  ))}
                </Box>
              );
            })()}
            {paySuccess && <Alert severity="success" sx={{ mb: 2 }}>{paySuccess}</Alert>}
            {/* Members tab — Show Inactive toggle */}
            {TABS[tab] === 'Members' && (isAdmin || isMemberManager) && (
              <Box sx={{
                mb: 2, p: 1.5, borderRadius: 2,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(201,168,76,0.15)',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
              }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                  Members
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveMembers}
                      onChange={e => setShowInactiveMembers(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Show inactive (canceled / past members)
                    </Typography>
                  }
                />
              </Box>
            )}
            {/* Customers tab — organizations renting court time */}
            {TABS[tab] === 'Customers' && isAdmin && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={<Switch size="small" checked={showArchivedCustomers} onChange={e => setShowArchivedCustomers(e.target.checked)} />}
                    label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Show archived</Typography>} />
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCustomerDialog(null)}
                    sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#b8973f' } }}>
                    Add Customer
                  </Button>
                </Box>
                {(() => {
                  const q = search.trim().toLowerCase();
                  const list = customers.filter(c => (showArchivedCustomers || !c.archived)).filter(c => !q ||
                    (c.organizationName || '').toLowerCase().includes(q) ||
                    `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(q) ||
                    (c.email || '').toLowerCase().includes(q));
                  if (list.length === 0) return (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.4)' }}>
                      <Typography variant="body2">{search ? 'No customers match your search.' : 'No customers yet. Add your first organization above.'}</Typography>
                    </Box>
                  );
                  return list.map(c => (
                    <Paper key={c.id} onClick={() => openCustomerDetail(c)}
                      sx={{ p: 2, mb: 1.5, borderRadius: 2, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,0.07)' } }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography sx={{ color: 'white', fontWeight: 700 }}>{c.organizationName}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                          {`${c.firstName || ''} ${c.lastName || ''}`.trim() || '—'}
                          {c.email ? ` · ${c.email}` : ''}{c.phone ? ` · ${c.phone}` : ''}
                        </Typography>
                      </Box>
                      {c.billingRateName && (
                        <Chip size="small" label={`${c.billingRateName}${c.billingRateHourly != null ? ` — $${c.billingRateHourly}/hr` : ''}`}
                          sx={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#C9A84C', fontWeight: 600 }} />
                      )}
                      {c.archived && (
                        <Chip size="small" label="Archived" sx={{ backgroundColor: 'rgba(127,140,141,0.3)', color: '#BDC3C7', fontWeight: 600 }} />
                      )}
                      <IconButton size="small" onClick={e => { e.stopPropagation(); setCustomerMenuTarget(c); setCustomerMenuAnchor(e.currentTarget); }} sx={{ color: 'white' }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ));
                })()}
              </Box>
            )}
            {TABS[tab] !== 'Customers' && (isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                {filtered.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.4)' }}>
                    <Typography variant="body2">No people match your filters.</Typography>
                  </Box>
                )}
                {filtered.map((person) => {
                  const memberType = getMemberType(person);
                  const status = getStatus(person);
                  const roles = person.roles || [];
                  const isStaffTab = TABS[tab] === 'Coaches';
                  const isCoachRole = roles.some(r => ['coach','assistant_coach'].includes(r));
                  const isContractorRole = roles.includes('contractor');
                  // Coach-pay UI applies only to actual coaches, even on the shared
                  // Staff tab. Contractors render as normal rows in Stage 1.
                  const isCoachTab = isStaffTab && isCoachRole;
                  const balance = person.sessionBalance;
                  const showBalance = (isAdmin || isMemberManager) && TABS[tab] !== 'Players' && !isStaffTab && balance !== undefined && balance !== null;

                  return (
                    <Box
                      key={person.id}
                      onClick={(e) => {
                        if (goesStraightToProfile(person)) {
                          e.stopPropagation();
                          viewProfileFor(person);
                        } else if (person.membership?.tierId && (person._collection === 'users' || person._memberUserId)) {
                          openMemberDetail(person, e);
                        } else if (person._collection === 'athletes' || person._collection === 'users') {
                          openAthleteDetail(person, e);
                        }
                      }}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(201,168,76,0.15)',
                        borderRadius: 2,
                        p: 1.25,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                        '&:active': { backgroundColor: 'rgba(201,168,76,0.12)' },
                        '&:hover': { backgroundColor: 'rgba(201,168,76,0.08)' },
                      }}
                    >
                      {/* Top row: avatar + name/email + kebab */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Avatar
                          src={person.photoURL || ''}
                          sx={{
                            width: 40, height: 40,
                            backgroundColor: 'rgba(201,168,76,0.25)',
                            color: '#C9A84C',
                            fontSize: '0.85rem',
                            border: '1px solid rgba(201,168,76,0.3)',
                            flexShrink: 0,
                          }}
                        >
                          {!person.photoURL && getInitials(person.firstName, person.lastName)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.95)', lineHeight: 1.2 }}>
                            {person.firstName} {person.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: 'rgba(255,255,255,0.5)',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {person.email || (person._collection === 'athletes' && person.athleteType === 'junior' ? (person.parentEmail || '—') : '—')}
                          </Typography>
                        </Box>
                        {/* Balance number, prominent */}
                        {showBalance && (
                          <Box sx={{ textAlign: 'right', flexShrink: 0, pl: 0.5 }}>
                            <Typography sx={{
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              lineHeight: 1,
                              color: (balance ?? 0) > 2 ? '#27AE60' : (balance ?? 0) > 0 ? '#E67E22' : '#E74C3C',
                            }}>
                              {isComped(person) ? 'Comp' : (balance ?? 0)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              sess
                            </Typography>
                          </Box>
                        )}
                        {isAdmin && (person.clinicCredits ?? 0) > 0 && (
                          <Box sx={{ textAlign: 'right', flexShrink: 0, pl: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1, color: '#A569BD' }}>
                              {person.clinicCredits}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              clinic
                            </Typography>
                          </Box>
                        )}
                        {isCoachTab && person._classesSincePayment != null && (
                          <Box sx={{ textAlign: 'right', flexShrink: 0, pl: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#C9A84C', lineHeight: 1 }}>
                              ${person._classesSincePayment.toFixed(0)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              owed
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Bottom row: chips */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1, alignItems: 'center' }}>
                        {/* Athlete type / skill (juniors & adults) */}
                        {person.athleteType && (
                          <Chip
                            label={person.athleteType === 'junior' ? 'Junior' : 'Adult'}
                            size="small"
                            sx={{
                              fontSize: '0.65rem', height: 20,
                              backgroundColor: person.athleteType === 'junior' ? 'rgba(127,179,211,0.2)' : 'rgba(201,168,76,0.2)',
                              color: person.athleteType === 'junior' ? '#7FB3D3' : '#E5C77B',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {person.skillGroup && (
                          <Chip
                            label={person.skillGroup}
                            size="small"
                            sx={{
                              fontSize: '0.65rem', height: 20,
                              backgroundColor: 'rgba(255,255,255,0.08)',
                              color: 'rgba(255,255,255,0.7)',
                              fontWeight: 500,
                            }}
                          />
                        )}
                        {/* Coach / contractor role chips (Staff tab) */}
                        {isStaffTab && (
                          <>
                            {roles.includes('coach') && <Chip label="Head Coach" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(30,58,95,0.5)', color: '#7FB3D3', fontWeight: 600 }} />}
                            {roles.includes('assistant_coach') && <Chip label="Asst Coach" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(142,68,173,0.3)', color: '#D7BDE2', fontWeight: 600 }} />}
                            {roles.includes('contractor') && <Chip label="Contractor" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(14,98,81,0.35)', color: '#7DCEA0', fontWeight: 600 }} />}
                            {roles.includes('volunteer') && <Chip label="Volunteer" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(127,140,141,0.3)', color: '#BDC3C7', fontWeight: 600 }} />}
                            {/* Additional roles (admin, parent, lead, player) + tier badge —
                                styled to match the coach tab's dark background. */}
                            {roles.includes('admin') && <Chip label="Admin" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(192,57,43,0.3)', color: '#F5B7B1', fontWeight: 600 }} />}
                            {roles.includes('parent') && <Chip label="Parent" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(39,174,96,0.3)', color: '#A9DFBF', fontWeight: 600 }} />}
                            {roles.includes('lead') && <Chip label="Lead" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(160,64,0,0.3)', color: '#EDBB99', fontWeight: 600 }} />}
                            {roles.includes('player') && <Chip label="Player" size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(33,97,140,0.3)', color: '#AED6F1', fontWeight: 600 }} />}
                            {(() => {
                              const { tierStyle, isMemberActive } = getRoleChipsConfig(person);
                              if (tierStyle) {
                                return (
                                  <Chip
                                    label={tierStyle.label}
                                    size="small"
                                    sx={{
                                      fontSize: '0.6rem', height: 20, letterSpacing: '0.5px',
                                      backgroundColor: tierStyle.bg,
                                      color: tierStyle.color,
                                      fontWeight: 700,
                                    }}
                                  />
                                );
                              }
                              if (isMemberActive && !roles.includes('member')) {
                                return (
                                  <Chip
                                    label="Member"
                                    size="small"
                                    sx={{
                                      fontSize: '0.65rem', height: 20,
                                      backgroundColor: 'rgba(201,168,76,0.3)',
                                      color: '#F9E79F',
                                      fontWeight: 600,
                                    }}
                                  />
                                );
                              }
                              return null;
                            })()}
                          </>
                        )}
                        {/* Member type & status chips (non-staff tabs) */}
                        {!isStaffTab && (
                          <>
                            {/* For athletes (no roles array): keep the single memberType chip.
                                For users: render every role from the roles array, suppressing
                                the generic 'member' chip when a tier badge will render. */}
                            {person._collection === 'athletes' ? (
                              <Chip
                                label={memberType}
                                size="small"
                                sx={{
                                  fontSize: '0.65rem', height: 20,
                                  backgroundColor: MEMBER_TYPE_COLORS[memberType]?.bg,
                                  color: MEMBER_TYPE_COLORS[memberType]?.color,
                                  fontWeight: 600,
                                }}
                              />
                            ) : (() => {
                              const { roles: visibleRoles, tierStyle, isMemberActive } = getRoleChipsConfig(person);
                              return (
                                <>
                                  {visibleRoles.map(role => (
                                    <Chip
                                      key={role}
                                      label={ROLE_LABELS[role] || role}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem', height: 20,
                                        backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0',
                                        color: ROLE_COLORS[role]?.color || '#555',
                                        fontWeight: 600,
                                      }}
                                    />
                                  ))}
                                  {/* Tier badge takes precedence over the generic 'member' chip
                                      (which is filtered out by getRoleChipsConfig when tierStyle exists). */}
                                  {tierStyle && (
                                    <Chip
                                      label={tierStyle.label}
                                      size="small"
                                      sx={{
                                        fontSize: '0.6rem', height: 20, letterSpacing: '0.5px',
                                        backgroundColor: tierStyle.bg,
                                        color: tierStyle.color,
                                        fontWeight: 700,
                                      }}
                                    />
                                  )}
                                  {/* Fallback: active membership but tier doesn't match any known
                                      style AND user has no 'member' role — show generic "Member". */}
                                  {!tierStyle && isMemberActive && !(person.roles || []).includes('member') && (
                                    <Chip
                                      label="Member"
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem', height: 20,
                                        backgroundColor: ROLE_COLORS.member.bg,
                                        color: ROLE_COLORS.member.color,
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                            <Chip
                              label={status}
                              size="small"
                              sx={{
                                fontSize: '0.65rem', height: 20,
                                backgroundColor: STATUS_COLORS[status]?.bg,
                                color: STATUS_COLORS[status]?.color,
                                fontWeight: 600,
                              }}
                            />
                          </>
                        )}
                        {/* Last check-in pushed to right */}
                        <Box sx={{ flex: 1 }} />
                        {person.lastCheckIn && (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
                            {formatDate(person.lastCheckIn)}
                          </Typography>
                        )}
                      </Box>

                      {/* Inline action buttons (Coach Pay, Lead Promote, Archived Restore) */}
                      {((isCoachTab && isCoachRole && !roles.includes('volunteer')) ||
                        (TABS[tab] === 'Leads' && isAdmin) ||
                        (TABS[tab] === 'Archived' && isAdmin)) && (
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, pt: 1, borderTop: '1px solid rgba(201,168,76,0.1)' }} onClick={e => e.stopPropagation()}>
                          {TABS[tab] === 'Leads' && isAdmin && (
                            <Button size="small" variant="contained"
                              onClick={e => handlePromoteToActive(person, e)}
                              sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontSize: '0.7rem', flex: 1 }}>
                              Promote to Active
                            </Button>
                          )}
                          {TABS[tab] === 'Archived' && isAdmin && (
                            <Button size="small" variant="outlined"
                              onClick={e => handleUnarchive(person, e)}
                              sx={{ borderColor: 'rgba(201,168,76,0.4)', color: 'rgba(201,168,76,0.9)', fontSize: '0.7rem', flex: 1 }}>
                              Restore
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
            <TableContainer sx={{ background: '#0D1E35' }}>
              <Table sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(201,168,76,0.08)', color: 'rgba(255,255,255,0.8)' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(201,168,76,0.15)' }}>
                    {TABS[tab] === 'Coaches' ? (
                      ['Name', 'Role', 'Outstanding', 'Last Check-in', ''].map(label => (
                        <TableCell key={label} sx={{ color: '#C9A84C', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '1px' }}>{label}</TableCell>
                      ))
                    ) : (
                      [
                        { col: 'name', label: 'Name' },
                        { col: 'memberType', label: getTypeLabel() },
                        { col: 'plan', label: 'Plan' },
                        { col: 'status', label: 'Status' },
                        ...((isAdmin || isMemberManager) && TABS[tab] !== 'Players' ? [{ col: 'sessions', label: 'Sessions' }] : []),
                        { col: 'lastCheckIn', label: 'Last Check-in' },
                      ].map(({ col, label }) => (
                        <TableCell key={col} align={col === 'sessions' ? 'center' : 'left'}
                          onClick={() => handleSort(col)}
                          sx={{ color: '#C9A84C', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '1px', cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'rgba(201,168,76,0.08)' } }}>
                          {label}<SortIcon col={col} />
                        </TableCell>
                      ))
                    )}
                    {TABS[tab] !== 'Coaches' && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((person, i) => {
                    const memberType = getMemberType(person);
                    const status = getStatus(person);
                    const roles = person.roles || [];
                    const rowBg = { backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(13,30,53,1)', '&:hover': { backgroundColor: 'rgba(201,168,76,0.08)', cursor: 'pointer' } };

                    if (TABS[tab] === 'Coaches') {
                      // ── Coaches row ─────────────────────────────────────────
                      const roleLabels = [
                        roles.includes('coach') ? 'Head Coach' : null,
                        roles.includes('assistant_coach') ? 'Asst Coach' : null,
                        roles.includes('contractor') ? 'Contractor' : null,
                        roles.includes('volunteer') ? 'Volunteer' : null,
                        roles.includes('admin') ? 'Admin' : null,
                        roles.includes('court_admin') ? 'Calendar Manager' : null,
                        roles.includes('member_manager') ? 'Member Manager' : null,
                      ].filter(Boolean);
                      const isPendingInvite = !!person.pendingLinkEmail;
                      const ROLE_STYLE = {
                        'Head Coach': { bg: 'rgba(30,58,95,0.5)', color: '#7FB3D3' },
                        'Asst Coach': { bg: 'rgba(142,68,173,0.3)', color: '#D7BDE2' },
                        'Contractor': { bg: 'rgba(14,98,81,0.35)', color: '#7DCEA0' },
                        'Volunteer':  { bg: 'rgba(127,140,141,0.3)', color: '#BDC3C7' },
                        'Admin':      { bg: 'rgba(192,57,43,0.3)', color: '#F5B7B1' },
                        'Calendar Manager': { bg: 'rgba(17,120,100,0.3)', color: '#76D7C4' },
                        'Member Manager':   { bg: 'rgba(147,81,22,0.3)', color: '#F0B27A' },
                        'Pending':    { bg: 'rgba(183,149,11,0.25)', color: '#F1C40F' },
                      };
                      const rowIsCoach = roles.some(r => ['coach','assistant_coach'].includes(r));
                      return (
                        <TableRow key={person.id} onClick={() => viewProfileFor(person)} sx={rowBg}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar src={person.photoURL || ''}
                                sx={{ width: 36, height: 36, backgroundColor: 'rgba(201,168,76,0.25)', color: '#C9A84C', fontSize: '0.85rem', border: '1px solid rgba(201,168,76,0.3)' }}>
                                {!person.photoURL && getInitials(person.firstName, person.lastName)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{person.firstName} {person.lastName}</Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{person.email || '—'}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {roleLabels.length > 0 ? roleLabels.map(r => (
                                <Chip key={r} label={r} size="small" sx={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: ROLE_STYLE[r].bg, color: ROLE_STYLE[r].color }} />
                              )) : <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>—</Typography>}
                              {isPendingInvite && (
                                <Chip label="Pending" size="small" sx={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: ROLE_STYLE['Pending'].bg, color: ROLE_STYLE['Pending'].color }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {rowIsCoach ? (
                              <Typography variant="body1" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                                {person._classesSincePayment != null ? `$${person._classesSincePayment.toFixed(2)}` : '—'}
                              </Typography>
                            ) : (
                              <Typography variant="body1" sx={{ fontWeight: 700, color: '#7DCEA0' }}>
                                {person._contractorOutstanding != null ? `$${person._contractorOutstanding.toFixed(2)}` : '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>{formatDate(person.lastCheckIn)}</Typography>
                          </TableCell>
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            <IconButton size="small" onClick={e => handleRowMenu(e, person)} sx={{ color: 'white' }}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // ── Standard row (all other tabs) ────────────────────────
                    return (
                      <TableRow
                        key={person.id}
                        onClick={(e) => {
                          if (goesStraightToProfile(person)) {
                            viewProfileFor(person);
                          } else if (person._collection === 'athletes' || person._collection === 'users') {
                            // Athletes keep the detail dialog instead of navigating away
                            openAthleteDetail(person, e);
                          }
                        }}
                        sx={{
                          backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(13,30,53,1)',
                          '&:hover': { backgroundColor: 'rgba(201,168,76,0.08)', cursor: 'pointer' },
                        }}
                      >
                        {/* Name + Email */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Tooltip title="Click to upload photo">
                              <Box sx={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => document.getElementById(`photo-upload-${person.id}`).click()}>
                                <Avatar
                                  src={person.photoURL || ''}
                                  sx={{ width: 36, height: 36, backgroundColor: 'rgba(201,168,76,0.25)', color: '#C9A84C', fontSize: '0.85rem', border: '1px solid rgba(201,168,76,0.3)' }}>
                                  {!person.photoURL && getInitials(person.firstName, person.lastName)}
                                </Avatar>
                                {uploadingPhotoId === person.id ? (
                                  <CircularProgress size={12} sx={{ position: 'absolute', bottom: 0, right: 0 }} />
                                ) : (
                                  <Box sx={{
                                    position: 'absolute', inset: 0, borderRadius: '50%',
                                    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', opacity: 0,
                                    '&:hover': { opacity: 1 }, transition: 'opacity 0.2s',
                                  }}>
                                    <PhotoCameraIcon sx={{ fontSize: 14, color: 'white' }} />
                                  </Box>
                                )}
                                <input
                                  id={`photo-upload-${person.id}`}
                                  type="file" accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={e => handlePhotoUpload(e, person)}
                                />
                              </Box>
                            </Tooltip>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                                {person.firstName} {person.lastName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                {person.email || (person._collection === 'athletes' && person.athleteType === 'junior' ? (person.parentEmail || '—') : '—')}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Type */}
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {person._collection === 'athletes' ? (
                              <Chip
                                label={memberType}
                                size="small"
                                sx={{
                                  backgroundColor: MEMBER_TYPE_COLORS[memberType]?.bg,
                                  color: MEMBER_TYPE_COLORS[memberType]?.color,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            ) : (() => {
                              const { roles: visibleRoles, tierStyle, isMemberActive } = getRoleChipsConfig(person);
                              return (
                                <>
                                  {visibleRoles.map(role => (
                                    <Chip
                                      key={role}
                                      label={ROLE_LABELS[role] || role}
                                      size="small"
                                      sx={{
                                        backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0',
                                        color: ROLE_COLORS[role]?.color || '#555',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                      }}
                                    />
                                  ))}
                                  {tierStyle && (
                                    <Chip
                                      label={tierStyle.label}
                                      size="small"
                                      sx={{
                                        backgroundColor: tierStyle.bg,
                                        color: tierStyle.color,
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        letterSpacing: '0.5px',
                                      }}
                                    />
                                  )}
                                  {!tierStyle && isMemberActive && !(person.roles || []).includes('member') && (
                                    <Chip
                                      label="Member"
                                      size="small"
                                      sx={{
                                        backgroundColor: ROLE_COLORS.member.bg,
                                        color: ROLE_COLORS.member.color,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                      }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </TableCell>

                        {/* Plan */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                              {person.plan || person.planName || '—'}
                            </Typography>
                            {hasActiveDayPass(person) && (
                              <Chip
                                label="Day Pass"
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(39,174,96,0.18)',
                                  color: '#27AE60',
                                  border: '1px solid rgba(39,174,96,0.45)',
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip label={status} size="small"
                            sx={{ backgroundColor: STATUS_COLORS[status]?.bg, color: STATUS_COLORS[status]?.color, fontWeight: 600, fontSize: '0.75rem' }} />
                        </TableCell>

                        {/* Session Balance — admin only, not shown on Players tab */}
                        {isAdmin && TABS[tab] !== 'Players' && (
                          <TableCell align="center">
                            {person.sessionBalance !== undefined && person.sessionBalance !== null ? (
                              <Typography variant="body1" sx={{
                                fontWeight: 'bold',
                                color: (person.sessionBalance ?? 0) > 2 ? '#27AE60' : (person.sessionBalance ?? 0) > 0 ? '#E67E22' : '#E74C3C'
                              }}>
                                {isComped(person) ? 'Comp' : (person.sessionBalance ?? 0)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.2)' }}>—</Typography>
                            )}
                          </TableCell>
                        )}

                        {/* Last Check-in */}
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                            {formatDate(person.lastCheckIn)}
                          </Typography>
                        </TableCell>

                        {/* Kebab + Coach Pay Buttons */}
                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                          {isAdmin && TABS[tab] === 'Leads' && (
                            <Button size="small" variant="contained"
                              onClick={e => handlePromoteToActive(person, e)}
                              sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontSize: '0.65rem', py: 0.3, mr: 0.5 }}>
                              Promote
                            </Button>
                          )}
                          {isAdmin && TABS[tab] === 'Archived' && (
                            <Button size="small" variant="outlined"
                              onClick={e => handleUnarchive(person, e)}
                              sx={{ borderColor: 'rgba(201,168,76,0.4)', color: 'rgba(201,168,76,0.8)', fontSize: '0.65rem', py: 0.3, mr: 0.5 }}>
                              Restore
                            </Button>
                          )}
                          <IconButton size="small" onClick={e => handleRowMenu(e, person)} sx={{ color: 'white' }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            ))}
            </>
          )}
        </Paper>

        {/* Customer row kebab menu */}
        <Menu anchorEl={customerMenuAnchor} open={Boolean(customerMenuAnchor)} onClose={() => setCustomerMenuAnchor(null)}>
          <MenuItem onClick={() => { setCustomerMenuAnchor(null); openCustomerDetail(customerMenuTarget); }}>View Profile</MenuItem>
          <MenuItem onClick={() => { setCustomerMenuAnchor(null); openCustomerDialog(customerMenuTarget); }}>Edit</MenuItem>
          {!customerMenuTarget?.archived && (
            <MenuItem onClick={() => { setCustomerMenuAnchor(null); handleArchiveCustomer(customerMenuTarget, true); }} sx={{ color: '#E67E22' }}>Archive</MenuItem>
          )}
          {customerMenuTarget?.archived && (
            <MenuItem onClick={() => { setCustomerMenuAnchor(null); handleArchiveCustomer(customerMenuTarget, false); }}>Restore</MenuItem>
          )}
          <MenuItem onClick={() => { setCustomerMenuAnchor(null); setCustomerDeleteTarget(customerMenuTarget); }} sx={{ color: '#E74C3C' }}>Delete</MenuItem>
        </Menu>

        {/* Customer detail dialog — only for organizations with no account of
            their own. Everyone else lands on the Company tab of their profile,
            which renders this same panel. */}
        <Dialog open={Boolean(customerDetailTarget)} onClose={() => setCustomerDetailTarget(null)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{customerDetailTarget?.organizationName}</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Customer · no linked account{customerDetailTarget?.archived ? ' · Archived' : ''}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <CompanyPanel customer={customerDetailTarget} onCustomerChange={applyCustomerSave} />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <IconButton
              onClick={e => {
                if (customerDetailTarget) {
                  setCustomerMenuTarget(customerDetailTarget);
                  setCustomerMenuAnchor(e.currentTarget);
                }
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Button onClick={() => setCustomerDetailTarget(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Customer add/edit — the same dialog the Company tab of a renter's
            profile opens from its Edit button. */}
        <CustomerEditDialog
          open={customerDialog}
          customer={customerEditTarget}
          onClose={() => setCustomerDialog(false)}
          onSaved={applyCustomerSave} />


        {/* Customer delete confirmation */}
        <Dialog open={Boolean(customerDeleteTarget)} onClose={() => setCustomerDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Customer?</DialogTitle>
          <DialogContent>
            <Typography>Delete <strong>{customerDeleteTarget?.organizationName}</strong>? This cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCustomerDeleteTarget(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDeleteCustomer}>Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Row kebab menu */}
        <Menu anchorEl={rowAnchor} open={Boolean(rowAnchor)} onClose={() => setRowAnchor(null)}>
          {/* Rows that already open the profile on click don't need View Profile here —
              they get the detail dialog instead, which is otherwise unreachable. */}
          {!goesStraightToProfile(selectedPerson) && (
            <MenuItem onClick={() => { setAthleteDetailDialog(false); setCoachDetailDialog(false); handleView(); }}>View Profile</MenuItem>
          )}
          {goesStraightToProfile(selectedPerson) && (() => {
            const selRoles = selectedPerson.roles || [];
            const isCoachSel = selectedPerson._collection !== 'athletes'
              && selRoles.some(r => ['coach', 'assistant_coach'].includes(r));
            const isContractorSel = selectedPerson._collection !== 'athletes' && selRoles.includes('contractor');
            // Where the row click already opens Contractor Details (see
            // viewProfileFor), this item offers the profile page instead.
            if (isContractorSel && !isCoachSel && TABS[tab] !== 'Parents') {
              return (
                <MenuItem onClick={() => { setRowAnchor(null); navigate(`/people/${selectedPerson.id}`); }}>
                  View Profile
                </MenuItem>
              );
            }
            return (
              <MenuItem onClick={(e) => {
                setRowAnchor(null);
                if (isCoachSel) openCoachDetail(selectedPerson, e);
                else if (isContractorSel) openContractorDetail(selectedPerson, e);
                else if (isMemberRow(selectedPerson)) openMemberDetail(selectedPerson, e);
                else openAthleteDetail(selectedPerson, e);
              }}>
                {isCoachSel ? 'Coach Details'
                  : isContractorSel ? 'Contractor Details'
                  : isMemberRow(selectedPerson) ? 'Membership Details'
                  : 'Session History'}
              </MenuItem>
            );
          })()}
          {canEditPeople && (
            <MenuItem onClick={() => { setAthleteDetailDialog(false); setCoachDetailDialog(false); handleEdit(); }}>Edit</MenuItem>
          )}
          {isAdmin && (
            <MenuItem onClick={() => { setAthleteDetailDialog(false); setCoachDetailDialog(false); handleManageRoles(); }}>Manage Roles</MenuItem>
          )}
          {TABS[tab] !== 'Archived' && canEditPeople && (
            <MenuItem onClick={() => { setAthleteDetailDialog(false); setCoachDetailDialog(false); setRowAnchor(null); setArchiveDialog(true); }} sx={{ color: '#E67E22' }}>Archive</MenuItem>
          )}
          {TABS[tab] === 'Archived' && canEditPeople && (
            <MenuItem onClick={(e) => { setAthleteDetailDialog(false); setCoachDetailDialog(false); setRowAnchor(null); handleUnarchive(selectedPerson, e); }}>Restore</MenuItem>
          )}
          {canEditPeople && (
            <MenuItem onClick={() => { setAthleteDetailDialog(false); setCoachDetailDialog(false); setRowAnchor(null); setDeleteDialog(true); }} sx={{ color: '#E74C3C' }}>Delete</MenuItem>
          )}
        </Menu>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete {selectedPerson?.firstName} {selectedPerson?.lastName}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
              This will permanently delete this person and cannot be undone.
            </Typography>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>

        {/* Archive confirmation dialog */}
        <Dialog open={archiveDialog} onClose={() => setArchiveDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Archive {selectedPerson?.firstName} {selectedPerson?.lastName}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
              This will hide them from all views. You can restore them from the Archived tab.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveDialog(false)}>Cancel</Button>
            <Button onClick={handleArchive} variant="contained" disabled={archiving}
              sx={{ backgroundColor: '#E67E22', color: 'white', '&:hover': { backgroundColor: '#D35400' } }}>
              {archiving ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Calc Dialog */}
        <Dialog open={calcDialog} onClose={() => setCalcDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#0D1E35', color: 'white', borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
            Calculate Amount Owed — {calcCoach?.firstName} {calcCoach?.lastName}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>Based on all non-canceled classes:</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Paper sx={{ flex: 1, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>Head Coach Sessions</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{calcSessions.head}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>{calcSessions.headHours} hrs × ${calcCoach?.headRatePerHour || 0}/hr</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 0.5 }}>= ${(calcSessions.headHours * (calcCoach?.headRatePerHour || 0)).toFixed(2)}</Typography>
              </Paper>
              <Paper sx={{ flex: 1, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>Assistant Sessions</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{calcSessions.assistant}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>{calcSessions.assistantHours} hrs × ${calcCoach?.assistantRatePerHour || 0}/hr</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 0.5 }}>= ${(calcSessions.assistantHours * (calcCoach?.assistantRatePerHour || 0)).toFixed(2)}</Typography>
              </Paper>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total Owed</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#E67E22' }}>
                ${((calcSessions.headHours * (calcCoach?.headRatePerHour || 0)) + (calcSessions.assistantHours * (calcCoach?.assistantRatePerHour || 0))).toFixed(2)}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCalcDialog(false)} sx={{ color: 'rgba(255,255,255,0.3)' }}>Cancel</Button>
            <Button variant="contained" onClick={applyCalc} disabled={calcSaving} sx={{ backgroundColor: 'rgba(201,168,76,0.15)' }}>
              {calcSaving ? 'Saving...' : 'Save Amount Owed'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contractor Detail Dialog */}
        <Dialog open={contractorDialog} onClose={() => setContractorDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ backgroundColor: '#0D1E35', color: 'white', borderBottom: '1px solid rgba(201,168,76,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {contractorPerson ? `${contractorPerson.firstName || ''} ${contractorPerson.lastName || ''}`.trim() : 'Contractor'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                Contractor{contractorPerson?.notes ? ` · ${contractorPerson.notes}` : ''}
              </Typography>
            </Box>
            <Chip label="Contractor" size="small" sx={{ backgroundColor: 'rgba(14,98,81,0.35)', color: '#7DCEA0', fontWeight: 600 }} />
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#0D1E35', pt: 2 }}>
            {contractorLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#C9A84C' }} /></Box>
            ) : (
              <>
                {/* Stat cards */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, mt: 1, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Rate', value: contractorPerson?.hourlyRate != null ? `$${Number(contractorPerson.hourlyRate).toFixed(2)}/hr` : '—', color: '#7FB3D3' },
                    { label: 'Pay Type', value: contractorPayType === 'hourly' ? 'Hourly' : contractorPayType === 'self_rate' ? 'Self-rate' : 'Per job', color: '#D7BDE2' },
                    { label: 'Outstanding', value: `$${contractorUnpaidTotal.toFixed(2)}`, color: '#E67E22' },
                    { label: 'Paid This Month', value: `$${contractorPaidThisMonth.toFixed(2)}`, color: '#27AE60' },
                    { label: 'Paid YTD', value: `$${contractorPaidYTD.toFixed(2)}`, color: '#7FB3D3' },
                  ].map(({ label, value, color }) => (
                    <Paper key={label} sx={{ flex: 1, minWidth: 90, p: 1.5, textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', fontSize: '0.65rem' }}>{label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color, fontSize: '1.1rem' }}>{value}</Typography>
                    </Paper>
                  ))}
                </Box>

                {/* Rate / pay type controls */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mb: 2, flexWrap: 'wrap' }}>
                  <TextField label="Rate" size="small" type="number" value={contractorRate}
                    onChange={e => setContractorRate(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { color: 'rgba(255,255,255,0.5)' } }}>$</InputAdornment> }}
                    sx={{ width: 130, '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.07)', color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }} />
                  <TextField select label="Payment Type" size="small" value={contractorPayType}
                    onChange={e => setContractorPayType(e.target.value)}
                    sx={{ width: 210, '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.07)', color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }, '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' } }}>
                    <MenuItem value="hourly">Admin set hourly rate</MenuItem>
                    <MenuItem value="per_job">Admin set per-job rate</MenuItem>
                    <MenuItem value="self_rate">Contractor set rate (approval)</MenuItem>
                  </TextField>
                  <Button size="small" variant="outlined" disabled={rateSaving} onClick={saveContractorRate}
                    sx={{ borderColor: '#C9A84C', color: '#C9A84C' }}>
                    {rateSaving ? '…' : 'Save'}
                  </Button>
                  <Box sx={{ ml: 'auto' }}>
                    <Button variant="contained" onClick={openLogWork}
                      sx={{ backgroundColor: '#0E6251', '&:hover': { backgroundColor: '#0B5345' }, fontWeight: 700 }}>
                      + Log Work
                    </Button>
                  </Box>
                </Box>

                {/* Work log */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                    Work Log ({selectedEntries.length > 0 ? `${selectedEntries.length} selected · $${selectedTotal.toFixed(2)}` : `${contractorEntries.length} entries`})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="text"
                      onClick={() => {
                        const sel = {};
                        contractorEntries.forEach(en => { if (!en.paid && en.approved !== false && en.amount != null) sel[en.id] = true; });
                        setSelectedEntryIds(sel);
                      }}
                      sx={{ color: 'rgba(201,168,76,0.7)', fontSize: '0.7rem', py: 0 }}>
                      Select Unpaid
                    </Button>
                    <Button size="small" variant="text"
                      onClick={() => setSelectedEntryIds({})}
                      sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', py: 0 }}>
                      Clear
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 1, p: 0.5 }}>
                {contractorEntries.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', py: 2, textAlign: 'center' }}>No work logged yet.</Typography>
                ) : (
                  contractorEntries.map(en => (
                    <Box key={en.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1, borderRadius: 1, mb: 0.5,
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', opacity: en.paid ? 0.55 : 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {!en.paid && en.approved !== false && en.amount != null && (
                          <Checkbox size="small" sx={{ p: 0.5, color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#C9A84C' } }}
                            checked={!!selectedEntryIds[en.id]}
                            onChange={e => setSelectedEntryIds(prev => ({ ...prev, [en.id]: e.target.checked }))} />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }} noWrap>
                            {en.description}
                            {en.paid && <Chip label="Paid" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', bgcolor: 'rgba(39,174,96,0.25)', color: '#7DCEA0' }} />}
                            {!en.paid && en.approved === false && en.amount != null && (
                              <Chip label="Needs approval" size="small" onClick={async () => {
                                await updateDoc(doc(db, 'workEntries', en.id), { approved: true });
                                setContractorEntries(prev => prev.map(x => x.id === en.id ? { ...x, approved: true } : x));
                              }} sx={{ ml: 1, height: 18, fontSize: '0.6rem', bgcolor: 'rgba(201,168,76,0.25)', color: '#C9A84C', cursor: 'pointer' }} />
                            )}
                            {!en.paid && en.amount == null && (
                              <Chip label="Unpriced" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', bgcolor: 'rgba(231,76,60,0.25)', color: '#F5B7B1' }} />
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            {en.date}{en.type === 'hours' && en.rate != null ? ` · ${en.hours}h × $${en.rate}` : en.type === 'task' ? ' · task' : en.hours ? ` · ${en.hours}h` : ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'white' }}>{en.amount != null ? `$${Number(en.amount).toFixed(2)}` : '—'}</Typography>
                        {!en.paid && (
                          <Button size="small" sx={{ minWidth: 0, px: 0.5, color: '#7FB3D3' }} onClick={() => openEditWork(en)}>Edit</Button>
                        )}
                        <Button size="small" sx={{ minWidth: 0, px: 0.5, color: '#E74C3C' }} onClick={() => deleteWorkEntry(en)}>Del</Button>
                      </Box>
                    </Box>
                  ))
                )}

                </Box>

                {/* Payment history */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#C9A84C', mb: 1, mt: 2 }}>Payment History</Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 1 }}>
                  {contractorPayments.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, color: 'rgba(255,255,255,0.3)' }}>No payments recorded.</Typography>
                  ) : contractorPayments.map(pm => (
                    <Box key={pm.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                          ${(pm.amount || 0).toFixed(2)}{pm.method ? ` via ${pm.method}` : ''}
                          {pm.entryCount ? <Typography component="span" variant="caption" sx={{ ml: 1, color: 'rgba(201,168,76,0.6)' }}>· {pm.entryCount} entr{pm.entryCount !== 1 ? 'ies' : 'y'}</Typography> : null}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          {/* Formatted from the YYYY-MM-DD string by hand: new Date() on
                              it is UTC midnight, which reads as the day before here. */}
                          Paid: {(() => { const [y, mo, d] = contractorPayDay(pm).split('-'); return y ? `${+mo}/${+d}/${y}` : '—'; })()}
                          {pm.note ? ` · ${pm.note}` : ''}
                        </Typography>
                      </Box>
                      <Chip label="Paid" size="small" sx={{ backgroundColor: 'rgba(39,174,96,0.2)', color: '#27AE60', fontSize: '0.7rem' }} />
                    </Box>
                  ))}
                </Box>

                {/* Pay selected */}
                {selectedTotal > 0 && (
                  <Box sx={{ p: 2, mt: 2, background: 'rgba(39,174,96,0.07)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: 2,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#27AE60' }}>
                      Pay Selected — ${selectedTotal.toFixed(2)} ({selectedEntries.length} {selectedEntries.length === 1 ? 'entry' : 'entries'})
                    </Typography>
                    {/* Opens the Pay Contractor dialog to pick the method. This
                        used to call payContractor directly, so every payment
                        was recorded as Venmo and the dialog was unreachable. */}
                    <Button variant="contained"
                      onClick={() => { setPayWorkForm({ method: 'Venmo', note: '', datePaid: localToday() }); setPayWorkOpen(true); }}
                      sx={{ backgroundColor: '#27AE60', fontWeight: 700, '&:hover': { backgroundColor: '#1E8449' } }}>
                      Record Payment
                    </Button>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Log Work Dialog */}
        <Dialog open={logWorkOpen} onClose={() => !logWorkSaving && setLogWorkOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{workEditId ? 'Edit Work Entry' : 'Log Work'}</DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            <RadioGroup row value={workForm.type} onChange={e => setWorkForm(f => ({ ...f, type: e.target.value }))} sx={{ mb: 1 }}>
              <FormControlLabel value="hours" control={<Radio size="small" />} label="Hours" />
              <FormControlLabel value="task" control={<Radio size="small" />} label="Task / flat" />
            </RadioGroup>
            {workForm.type === 'hours' ? (
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <TextField label="Hours" type="number" size="small" value={workForm.hours}
                  onChange={e => setWorkForm(f => ({ ...f, hours: e.target.value }))} sx={{ flex: 1 }} />
                <TextField label="Rate" type="number" size="small" value={workForm.rate}
                  onChange={e => setWorkForm(f => ({ ...f, rate: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ flex: 1 }} />
              </Box>
            ) : (
              <TextField label="Amount" type="number" size="small" fullWidth value={workForm.amount}
                onChange={e => setWorkForm(f => ({ ...f, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ mb: 1.5 }} />
            )}
            <TextField label="Description" size="small" fullWidth value={workForm.description}
              onChange={e => setWorkForm(f => ({ ...f, description: e.target.value }))} sx={{ mb: 1.5 }} />
            <TextField label="Date" type="date" size="small" fullWidth value={workForm.date}
              onChange={e => setWorkForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            {workForm.type === 'hours' && workForm.hours && workForm.rate && (
              <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: '#0E6251' }}>
                Total: ${(Math.round((parseFloat(workForm.hours) || 0) * (parseFloat(workForm.rate) || 0) * 100) / 100).toFixed(2)}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={logWorkSaving} onClick={() => setLogWorkOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={logWorkSaving} onClick={saveWorkEntry}
              sx={{ backgroundColor: '#0E6251', '&:hover': { backgroundColor: '#0B5345' } }}>
              {logWorkSaving ? 'Saving…' : 'Save Entry'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pay Contractor Dialog */}
        <Dialog open={payWorkOpen} onClose={() => !payWorkSaving && setPayWorkOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Pay Contractor</DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Paying <strong>{contractorPerson ? `${contractorPerson.firstName || ''} ${contractorPerson.lastName || ''}`.trim() : ''}</strong> for {selectedEntries.length} entr{selectedEntries.length === 1 ? 'y' : 'ies'}:
              <Box component="span" sx={{ display: 'block', fontSize: '1.4rem', fontWeight: 'bold', color: '#0E6251', mt: 0.5 }}>
                ${selectedTotal.toFixed(2)}
              </Box>
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Method</InputLabel>
              <Select value={payWorkForm.method} label="Method" onChange={e => setPayWorkForm(f => ({ ...f, method: e.target.value }))}>
                {['Venmo', 'Zelle', 'Cash', 'Check', 'Other'].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Date paid" type="date" size="small" fullWidth sx={{ mb: 1.5 }}
              InputLabelProps={{ shrink: true }} value={payWorkForm.datePaid}
              onChange={e => setPayWorkForm(f => ({ ...f, datePaid: e.target.value }))}
              error={!payWorkForm.datePaid} helperText={!payWorkForm.datePaid ? 'Required' : ''} />
            <TextField label="Note (optional)" size="small" fullWidth value={payWorkForm.note}
              onChange={e => setPayWorkForm(f => ({ ...f, note: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button disabled={payWorkSaving} onClick={() => setPayWorkOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={payWorkSaving || !payWorkForm.datePaid} onClick={payContractor}
              sx={{ backgroundColor: '#0E6251', '&:hover': { backgroundColor: '#0B5345' } }}>
              {payWorkSaving ? 'Recording…' : `Record $${selectedTotal.toFixed(2)}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pay Dialog */}
        <Dialog open={payDialog} onClose={() => setPayDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#27AE60', color: 'white' }}>
            Record Payment — {payCoach?.firstName} {payCoach?.lastName}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>Balance Outstanding</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#E67E22' }}>
                ${Math.max(0, (payCoach?.totalOwed || 0) - (payCoach?.totalPaid || 0)).toFixed(2)}
              </Typography>
            </Box>
            <TextField fullWidth label="Amount *" type="number" value={payForm.amount}
              onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
              inputProps={{ min: 0.01, step: 0.01 }} sx={{ mb: 2 }} />
            <TextField fullWidth select label="Payment Method" value={payForm.method}
              onChange={e => setPayForm(p => ({ ...p, method: e.target.value }))} sx={{ mb: 2 }}>
              {['Venmo', 'Zelle'].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Note (optional)" value={payForm.note}
              onChange={e => setPayForm(p => ({ ...p, note: e.target.value }))}
              placeholder="e.g. March sessions" sx={{ mb: 2 }} />
              {payCoach?.venmoUsername && payForm.method === 'Venmo' && (
              <Button fullWidth variant="outlined"
                onClick={() => {
                  const u = payCoach.venmoUsername.replace('@', '');
                  const note = encodeURIComponent(payForm.note || 'Torch Beach VB coaching payment');
                  window.open(`venmo://paycharge?txn=pay&recipients=${u}&amount=${parseFloat(payForm.amount) || 0}&note=${note}`, '_blank');
                }}
                sx={{ borderColor: '#3D95CE', color: '#3D95CE', fontWeight: 600 }}>
                Open in Venmo (@{payCoach.venmoUsername.replace('@', '')})
              </Button>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setPayDialog(false)} sx={{ color: 'rgba(255,255,255,0.3)' }}>Cancel</Button>
            <Button variant="contained" onClick={handlePay} disabled={paySaving}
              sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' } }}>
              {paySaving ? 'Saving...' : 'Record Payment'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage Roles Dialog */}
        <Dialog open={rolesDialog} onClose={() => setRolesDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#0D1E35', color: 'white', borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
            Manage Roles — {rolesTarget?.firstName} {rolesTarget?.lastName}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              Select all roles that apply to this user.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {AVAILABLE_ROLES.map(role => (
                <Chip
                  key={role}
                  label={role.replace('_', ' ')}
                  onClick={() => toggleRole(role)}
                  variant={selectedRoles.includes(role) ? 'filled' : 'outlined'}
                  sx={{
                    textTransform: 'capitalize',
                    cursor: 'pointer',
                    backgroundColor: selectedRoles.includes(role) ? '#1E3A5F' : 'transparent',
                    color: selectedRoles.includes(role) ? 'white' : '#1E3A5F',
                    borderColor: 'rgba(201,168,76,0.4)',
                    '&:hover': { backgroundColor: selectedRoles.includes(role) ? '#2E6DA4' : '#DEEAF1' },
                  }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setRolesDialog(false)} sx={{ color: 'rgba(255,255,255,0.3)' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveRoles} disabled={rolesSaving}
              sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}>
              {rolesSaving ? 'Saving...' : 'Save Roles'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Coach Detail Dialog */}
        <Dialog open={coachDetailDialog} onClose={() => setCoachDetailDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ backgroundColor: '#0D1E35', color: 'white', borderBottom: '1px solid rgba(201,168,76,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {coachDetailPerson?.firstName} {coachDetailPerson?.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                {(coachDetailPerson?.roles || [])
                  .filter(r => ['coach', 'assistant_coach', 'volunteer'].includes(r))
                  .map(r => r === 'coach' ? 'Head Coach' : r === 'assistant_coach' ? 'Asst Coach' : 'Volunteer')
                  .join(' · ')}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#0D1E35', pt: 2 }}>
            {coachDetailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#C9A84C' }} /></Box>
            ) : (
              <>
                {/* Class breakdown cards */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Head Coach', value: `${coachClassCounts.head} hrs`, color: '#7FB3D3' },
                    { label: 'Assistant', value: `${coachClassCounts.assistant} hrs`, color: '#D7BDE2' },
                    { label: 'Private', value: `${coachClassCounts.private} hrs`, color: '#F9E79F' },
                    { label: 'Outstanding', value: `$${coachClassesSincePayment.toFixed(2)}`, color: '#E67E22' },
                    { label: 'Paid This Month', value: `$${coachPaidThisMonth.toFixed(2)}`, color: '#27AE60' },
                    { label: 'Paid YTD', value: `$${coachPaidYTD.toFixed(2)}`, color: '#7FB3D3' },
                  ].map(({ label, value, color }) => (
                    <Paper key={label} sx={{ flex: 1, minWidth: 90, p: 1.5, textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', fontSize: '0.65rem' }}>{label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color, fontSize: '1.1rem' }}>{value}</Typography>
                    </Paper>
                  ))}
                </Box>

                {/* Inline pay form — admin only */}
                {isAdmin && (
                <Box sx={{ p: 2, mb: 3, background: 'rgba(39,174,96,0.07)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#27AE60', mb: 1.5 }}>Record Payment</Typography>
                  {detailPaySuccess && <Alert severity="success" sx={{ mb: 1.5, py: 0 }}>{detailPaySuccess}</Alert>}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField label="Amount" type="number" size="small" value={detailPayForm.amount}
                      onChange={e => setDetailPayForm(f => ({ ...f, amount: e.target.value }))}
                      inputProps={{ min: 0.01, step: 0.01 }}
                      sx={{ width: 110, '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.07)', color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }} />
                    <TextField select label="Method" size="small" value={detailPayForm.method}
                      onChange={e => setDetailPayForm(f => ({ ...f, method: e.target.value }))}
                      sx={{ width: 120, '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.07)', color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }}>
                      {['Venmo', 'Zelle', 'Cash', 'Check'].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </TextField>
                    <TextField label="Date Paid" type="date" size="small" value={detailPayForm.datePaid}
                      onChange={e => setDetailPayForm(f => ({ ...f, datePaid: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.07)', color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }} />
                    <TextField label="Note (optional)" size="small" value={detailPayForm.note}
                      onChange={e => setDetailPayForm(f => ({ ...f, note: e.target.value }))}
                      placeholder="e.g. April classes"
                      sx={{ flex: 1, minWidth: 140, '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.07)', color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }} />
                    <Button variant="contained" onClick={handleDetailPay} disabled={detailPaySaving || !detailPayForm.amount}
                      sx={{ backgroundColor: '#27AE60', color: 'white', fontWeight: 700, '&:hover': { backgroundColor: '#1E8449' }, whiteSpace: 'nowrap' }}>
                      {detailPaySaving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Record Payment'}
                    </Button>
                  </Box>
                    {coachDetailPerson?.venmoUsername && detailPayForm.method === 'Venmo' && (
                    <Button size="small" variant="outlined" sx={{ mt: 1, borderColor: '#3D95CE', color: '#3D95CE' }}
                      onClick={() => {
                        const u = coachDetailPerson.venmoUsername.replace('@', '');
                        const note = encodeURIComponent(detailPayForm.note || 'Torch Beach VB coaching payment');
                        window.open(`venmo://paycharge?txn=pay&recipients=${u}&amount=${parseFloat(detailPayForm.amount) || 0}&note=${note}`, '_blank');
                      }}>
                      Open in Venmo (@{coachDetailPerson.venmoUsername.replace('@', '')})
                    </Button>
                  )}
                </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                    Classes Coached ({coachDetailClasses.filter(c => selectedClassIds[c.id]).length} selected · ${selectedClassesTotal.toFixed(2)})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="text"
                      onClick={() => {
                        const s = {};
                        coachDetailClasses.forEach(c => { if (!paidClassIds.has(c.id)) s[c.id] = true; });
                        setSelectedClassIds(s);
                      }}
                      sx={{ color: 'rgba(201,168,76,0.7)', fontSize: '0.7rem', py: 0 }}>
                      Select Unpaid
                    </Button>
                    <Button size="small" variant="text"
                      onClick={() => setSelectedClassIds({})}
                      sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', py: 0 }}>
                      Clear
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 3, border: '1px solid rgba(201,168,76,0.1)', borderRadius: 1 }}>
                  {coachDetailClasses.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, color: 'rgba(255,255,255,0.3)' }}>No classes found.</Typography>
                  ) : coachDetailClasses.map(c => {
                    const isPaid = paidClassIds.has(c.id);
                    const isSelected = !!selectedClassIds[c.id];
                    return (
                      <Box key={c.id}
                        onClick={() => {
                          if (isPaid) return;
                          setSelectedClassIds(prev => {
                            const next = { ...prev };
                            if (next[c.id]) delete next[c.id]; else next[c.id] = true;
                            return next;
                          });
                        }}
                        sx={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1,
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          cursor: isPaid ? 'default' : 'pointer',
                          backgroundColor: isPaid ? 'rgba(39,174,96,0.05)' : (isSelected ? 'rgba(201,168,76,0.08)' : 'transparent'),
                          opacity: isPaid ? 0.6 : 1,
                          '&:hover': isPaid ? {} : { backgroundColor: 'rgba(201,168,76,0.12)' },
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={isPaid}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setSelectedClassIds(prev => {
                              const next = { ...prev };
                              if (e.target.checked) next[c.id] = true; else delete next[c.id];
                              return next;
                            })}
                            sx={{ p: 0, color: 'rgba(201,168,76,0.5)', '&.Mui-checked': { color: '#C9A84C' } }}
                          />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                              {c.date} · {c.startTime}–{c.endTime}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                              {(c.sessionType || '').replace(/-/g, ' ')}{c.skillGroup ? ` · ${c.skillGroup}` : ''}
                              {' · '}
                              <Box component="span" sx={{ color: (coachClassAttendees[c.id] || 0) === 0 ? 'rgba(255,255,255,0.3)' : '#C9A84C' }}>
                                ({coachClassAttendees[c.id] || 0} attended)
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {isPaid && (
                            <Chip label="Paid" size="small" sx={{
                              fontSize: '0.65rem', fontWeight: 600, height: 20,
                              backgroundColor: 'rgba(39,174,96,0.2)', color: '#27AE60',
                            }} />
                          )}
                          <Chip label={c.role} size="small" sx={{
                            fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: c.role === 'Head Coach' ? 'rgba(30,58,95,0.5)' : 'rgba(142,68,173,0.3)',
                            color: c.role === 'Head Coach' ? '#7FB3D3' : '#D7BDE2',
                          }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {coachWorkEntries.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7DCEA0', mb: 1 }}>Tournament / Other Work</Typography>
                    <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(125,206,160,0.15)', borderRadius: 1, mb: 2 }}>
                      {coachWorkEntries.map(w => (
                        <Box key={w.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', opacity: w.paid ? 0.5 : 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!w.paid && (
                              <Checkbox size="small" sx={{ p: 0.25, color: 'rgba(125,206,160,0.6)', '&.Mui-checked': { color: '#7DCEA0' } }}
                                checked={!!selectedWorkIds[w.id]}
                                onChange={e => setSelectedWorkIds(prev => ({ ...prev, [w.id]: e.target.checked }))} />
                            )}
                            <Box>
                              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                {w.description}
                                {w.paid && <Chip label="Paid" size="small" sx={{ ml: 1, height: 16, fontSize: '0.55rem', bgcolor: 'rgba(46,204,113,0.2)', color: '#7DCEA0' }} />}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                {w.date}{w.type === 'hours' ? ` · ${w.hours}h × $${w.rate}` : ' · task'}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#7DCEA0', fontWeight: 700 }}>${(w.amount || 0).toFixed(2)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#C9A84C', mb: 1 }}>Payment History</Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 1 }}>
                  {coachDetailPayments.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, color: 'rgba(255,255,255,0.3)' }}>No payments recorded.</Typography>
                  ) : coachDetailPayments.map(p => {
                    const dateRange = (p.classDates && p.classDates.length)
                      ? (p.classDates.length === 1 ? p.classDates[0] : `${p.classDates[0]} → ${p.classDates[p.classDates.length - 1]}`)
                      : '';
                    return (
                      <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                            ${(p.amount || 0).toFixed(2)} via {p.method}
                            {p.classCount ? <Typography component="span" variant="caption" sx={{ ml: 1, color: 'rgba(201,168,76,0.6)' }}>· {p.classCount} class{p.classCount !== 1 ? 'es' : ''}</Typography> : null}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            Paid: {p.datePaid || (p.paidAt ? new Date(p.paidAt.seconds * 1000).toLocaleDateString() : '—')}
                            {dateRange ? ` · covers ${dateRange}` : ''}
                            {p.note ? ` · ${p.note}` : ''}
                          </Typography>
                        </Box>
                        <Chip label="Paid" size="small" sx={{ backgroundColor: 'rgba(39,174,96,0.2)', color: '#27AE60', fontSize: '0.7rem' }} />
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#0D1E35', borderTop: '1px solid rgba(201,168,76,0.1)', p: 2, justifyContent: 'space-between' }}>
            <IconButton
              size="small"
              onClick={(e) => {
                if (coachDetailPerson) {
                  setSelectedPerson(coachDetailPerson);
                  setRowAnchor(e.currentTarget);
                }
              }}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <MoreVertIcon />
            </IconButton>
            <Button onClick={() => setCoachDetailDialog(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Athlete/Parent Detail Dialog */}
        <Dialog open={athleteDetailDialog} onClose={() => setAthleteDetailDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {athleteDetailPerson?.firstName} {athleteDetailPerson?.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {athleteDetailPerson?._collection === 'athletes' ? `${athleteDetailPerson?.athleteType || 'Athlete'}` : 'User'} · Session History
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#1E3A5F', pt: 2 }}>
            {athleteDetailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: 'white' }} />
              </Box>
            ) : (
              <>
                {/* Session summary cards */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                  {/* Total Sessions */}
                  <Paper sx={{ flex: 1, minWidth: 120, p: 1.5, textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem' }}>Total Sessions</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#3498DB', fontSize: '1.1rem' }}>{athleteSessionHistory.length}</Typography>
                  </Paper>

                  {/* Current Balance */}
                  {(() => {
                    const bal = athleteDetailPerson?.sessionBalance || 0;
                    const balColor = bal > 2 ? '#27AE60' : bal > 0 ? '#E67E22' : '#E74C3C';
                    return (
                      <Paper sx={{ flex: 1, minWidth: 120, p: 1.5, textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem' }}>Current Balance</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: balColor, fontSize: '1.1rem' }}>{bal}</Typography>
                      </Paper>
                    );
                  })()}

                  {/* Last Check-In */}
                  <Paper sx={{ flex: 1, minWidth: 120, p: 1.5, textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', fontSize: '0.7rem' }}>Last Check-In</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#9B59B6', fontSize: '1.1rem' }}>
                      {athleteSessionHistory.length > 0
                        ? (athleteSessionHistory[0].checkedInAt ? new Date(athleteSessionHistory[0].checkedInAt).toLocaleDateString() : athleteSessionHistory[0].classDetails?.date)
                        : '—'}
                    </Typography>
                  </Paper>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', mb: 1.5 }}>Session History</Typography>
                <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}>
                  {athleteSessionHistory.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 3, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                      No session history found.
                    </Typography>
                  ) : athleteSessionHistory.map((session, index) => {
                    const classDetails = session.classDetails;
                    const checkedInDate = session.checkedInAt ? new Date(session.checkedInAt) : null;
                    const classDeleted = !classDetails;
                    const displayDate = classDetails?.date || session.date || '—';
                    const displaySessionType = classDetails?.sessionType || session.sessionType;
                    
                    return (
                      <Box key={session.id} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        px: 2, 
                        py: 1.5, 
                        borderBottom: index < athleteSessionHistory.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                        backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                            {displayDate}
                            {classDetails?.startTime && classDetails?.endTime && ` · ${classDetails.startTime}–${classDetails.endTime}`}
                            {classDetails?.courtNumber && ` · Court ${classDetails.courtNumber}`}
                            {classDeleted && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                                (class deleted)
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {formatSessionType(displaySessionType)}
                            {classDetails?.skillGroup ? ` · ${classDetails.skillGroup}` : ''}
                          </Typography>
                          {checkedInDate && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
                              Checked in: {checkedInDate.toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', ml: 2 }}>
                          {session.balanceBefore !== undefined && session.balanceAfter !== undefined && (
                            <Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                Balance: {session.balanceBefore} → {session.balanceAfter}
                              </Typography>
                            </Box>
                          )}
                          <Chip 
                            label="Attended" 
                            size="small" 
                            sx={{ 
                              backgroundColor: 'rgba(39,174,96,0.2)', 
                              color: '#27AE60', 
                              fontSize: '0.7rem',
                              mt: 0.5
                            }} 
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#1E3A5F', borderTop: '1px solid rgba(255,255,255,0.1)', p: 2, justifyContent: 'space-between' }}>
            <IconButton
              size="small"
              onClick={(e) => {
                if (athleteDetailPerson) {
                  setSelectedPerson(athleteDetailPerson);
                  setRowAnchor(e.currentTarget);
                }
              }}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <MoreVertIcon />
            </IconButton>
            <Button onClick={() => setAthleteDetailDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Find Duplicates Dialog */}
        <Dialog open={dupsDialog} onClose={() => !dupsMerging && setDupsDialog(false)} maxWidth="md" fullWidth
          PaperProps={{ sx: { backgroundColor: '#0D1E35', color: 'white', border: '1px solid rgba(201,168,76,0.2)' } }}>
          <DialogTitle sx={{ color: '#C9A84C', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px' }}>
            Find Duplicate Athletes
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(201,168,76,0.15)' }}>
            {dupsLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#C9A84C' }} />
                <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  Scanning athletes and attendance records...
                </Typography>
              </Box>
            ) : (
              <>
                {dupsMsg && (
                  <Alert severity={dupsMsg.startsWith('Merged') ? 'success' : dupsMsg.includes('failed') || dupsMsg.includes('Failed') ? 'error' : 'info'} sx={{ mb: 2 }}>
                    {dupsMsg}
                  </Alert>
                )}
                {dupsGroups.length === 0 ? (
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 4 }}>
                    No duplicate athletes found.
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', mb: 2 }}>
                      Found {dupsGroups.length} name match{dupsGroups.length > 1 ? 'es' : ''}. "Likely" groups share an email or parent; "Possible" groups share only a name - could be one person with two emails, or two different people, so review before merging. Pick which to keep; attendance from the others is reassigned to the keeper and they are deleted.
                    </Typography>
                    {dupsGroups.map(group => {
                      const keeperId = dupsKeeper[group.key];
                      return (
                        <Paper key={group.key} elevation={0} sx={{
                          backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,168,76,0.15)',
                          borderRadius: 2, p: 2, mb: 2,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ color: '#C9A84C', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                              {`${group.docs[0].firstName || ''} ${group.docs[0].lastName || ''}`.trim() || 'Unknown'}
                            </Typography>
                            <Chip
                              label={group.reason}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.6rem',
                                backgroundColor: group.kind === 'possible' ? 'rgba(243,156,18,0.2)' : 'rgba(39,174,96,0.2)',
                                color: group.kind === 'possible' ? '#F39C12' : '#27AE60',
                              }}
                            />
                          </Box>
                          <RadioGroup
                            value={keeperId || ''}
                            onChange={e => setDupsKeeper(prev => ({ ...prev, [group.key]: e.target.value }))}
                          >
                            {group.docs.map(d => (
                              <FormControlLabel
                                key={d.id}
                                value={d.id}
                                control={<Radio sx={{ color: 'rgba(201,168,76,0.5)', '&.Mui-checked': { color: '#C9A84C' } }} />}
                                label={
                                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', py: 0.5 }}>
                                    <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                                      {d.firstName} {d.lastName}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                                      {d.email || 'no email'}
                                    </Typography>
                                    <Chip
                                      label={d.athleteType || 'no type'}
                                      size="small"
                                      sx={{
                                        height: 20, fontSize: '0.65rem',
                                        backgroundColor: d.athleteType === 'junior' ? 'rgba(39,174,96,0.2)' : d.athleteType === 'adult' ? 'rgba(52,152,219,0.2)' : 'rgba(255,255,255,0.1)',
                                        color: d.athleteType === 'junior' ? '#27AE60' : d.athleteType === 'adult' ? '#3498DB' : 'rgba(255,255,255,0.5)',
                                      }}
                                    />
                                    {d.isArchived && (
                                      <Chip label="Archived" size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: 'rgba(231,76,60,0.2)', color: '#E74C3C' }} />
                                    )}
                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                                      Balance: {d.sessionBalance || 0}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                                      {d._attendanceCount} check-in{d._attendanceCount === 1 ? '' : 's'}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                                      {d.id.slice(0, 8)}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ m: 0, mb: 0.5 }}
                              />
                            ))}
                          </RadioGroup>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button
                              size="small" variant="contained" disabled={dupsMerging || !keeperId}
                              onClick={() => handleMergeDuplicate(group)}
                              sx={{ backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}
                            >
                              {dupsMerging ? 'Merging...' : 'Merge this group'}
                            </Button>
                          </Box>
                        </Paper>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(201,168,76,0.15)' }}>
            <Button onClick={() => setDupsDialog(false)} disabled={dupsMerging} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Close
            </Button>
            <Button onClick={handleFindDuplicates} disabled={dupsLoading || dupsMerging} sx={{ color: '#C9A84C' }}>
              Rescan
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Member detail dialog: membership card + admin manage + purchases ── */}
        <Dialog open={memberDetailDialog} onClose={() => setMemberDetailDialog(false)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { backgroundColor: '#12263F', color: 'white', border: '1px solid rgba(201,168,76,0.3)' } }}>
          {memberDetail && (() => {
            const mem = memberDetail.membership || {};
            const fmtD = (v) => {
              if (!v) return '\u2014';
              const d = v.toDate ? v.toDate() : new Date(v);
              return isNaN(d) ? '\u2014' : d.toLocaleDateString();
            };
            const hasStripe = !!mem.stripeSubscriptionId;
            const isAnnualLike = /annual|year/i.test(mem.tierName || '');
            const freezeActive = !!mem.freeze;
            const yearNow = new Date().getFullYear();
            const freezesUsed = (mem.freezeHistory || []).filter(h => {
              const t = h.requestedAt?.toDate ? h.requestedAt.toDate() : new Date(h.requestedAt);
              return t.getFullYear() === yearNow;
            }).length;
            // Effective status, not the stored one: membership.status is written at
            // purchase and never rewritten, so a lapsed day pass still says
            // 'active' in Firestore and would display as Active here.
            const effStatus = membershipStatus(memberDetail);
            const statusChip = mem.cancelAtPeriodEnd
              ? { label: `Cancels ${fmtD(mem.cancelAt || mem.currentPeriodEnd)}`, bg: '#E67E22' }
              : effStatus === 'frozen' ? { label: `Frozen until ${fmtD(mem.freeze?.endsAt)}`, bg: '#5D6D7E' }
              : effStatus === 'trialing' ? { label: 'Active (prepaid)', bg: '#2E86C1' }
              : effStatus === 'expired' ? { label: `Expired ${fmtD(mem.expiresAt)}`, bg: '#C0392B' }
              : effStatus === 'active' ? { label: 'Active', bg: '#27AE60' }
              : { label: effStatus || 'No status', bg: '#C0392B' };
            const myPurchases = allPurchases
              .filter(p => p.userId === memberDetail._targetUserId)
              .sort((a, b) => {
                const ta = a.purchaseDate?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? a.timestamp?.toMillis?.() ?? 0;
                const tb = b.purchaseDate?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? b.timestamp?.toMillis?.() ?? 0;
                return tb - ta;
              });
            return (
              <>
                <DialogTitle sx={{ color: '#C9A84C', fontWeight: 700 }}>
                  {`${memberDetail.firstName || ''} ${memberDetail.lastName || ''}`.trim() || memberDetail.email}
                  <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.5)' }}>{memberDetail.email}</Typography>
                </DialogTitle>
                <DialogContent>
                  {memberMsg && <Alert severity={memberMsg.severity} onClose={() => setMemberMsg(null)} sx={{ mb: 2 }}>{memberMsg.text}</Alert>}

                  <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.25)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, color: 'white' }}>{mem.tierName || 'Membership'}</Typography>
                      <Chip size="small" label={statusChip.label} sx={{ backgroundColor: statusChip.bg, color: 'white', fontWeight: 700 }} />
                      {mem.migratedFrom && <Chip size="small" label="migrated" sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }} />}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Next billing: {fmtD(mem.currentPeriodEnd)}
                      {' \u00b7 '}Regular {mem.weeklyRegularHours ?? 0}h / Prime {mem.weeklyPrimeHours ?? 0}h weekly
                    </Typography>
                    {freezeActive && (
                      <Typography variant="body2" sx={{ color: '#E67E22', mt: 0.5 }}>
                        Freeze: {fmtD(mem.freeze.startsAt)} → {fmtD(mem.freeze.endsAt)}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
                      {hasStripe ? `Stripe: ${mem.stripeSubscriptionId} \u00b7 Freezes used ${freezesUsed}/2 this year` : 'No Stripe subscription (manual/comped membership)'}
                    </Typography>
                  </Paper>

                  {hasStripe && canEditPeople && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                      {mem.cancelAtPeriodEnd ? (
                        <Button size="small" variant="contained" disabled={memberBusy} onClick={() => memberAction('uncancel')} sx={{ backgroundColor: '#27AE60' }}>Undo Cancel</Button>
                      ) : freezeActive ? (
                        <Button size="small" variant="outlined" disabled={memberBusy} onClick={() => memberAction('unfreeze')} sx={{ color: '#C9A84C', borderColor: '#C9A84C' }}>Remove Freeze</Button>
                      ) : (
                        <>
                          {!isAnnualLike && (
                            <>
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <InputLabel sx={{ color: 'rgba(255,255,255,0.6)' }}>Months</InputLabel>
                                <Select value={memberFreezeMonths} label="Months" onChange={e => setMemberFreezeMonths(e.target.value)}
                                  sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.4)' } }}>
                                  {[1, 2, 3].map(m => <MenuItem key={m} value={m}>{m} month{m > 1 ? 's' : ''}</MenuItem>)}
                                </Select>
                              </FormControl>
                              <Button size="small" variant="outlined" disabled={memberBusy || freezesUsed >= 2}
                                onClick={() => memberAction('freeze', memberFreezeMonths)}
                                sx={{ color: '#C9A84C', borderColor: '#C9A84C' }}>Freeze</Button>
                            </>
                          )}
                          <Button size="small" variant="outlined" color="error" disabled={memberBusy}
                            onClick={() => { if (window.confirm(`Cancel ${memberDetail.firstName || 'this member'}'s membership at period end?`)) memberAction('cancel'); }}>
                            Cancel Membership
                          </Button>
                        </>
                      )}
                      {memberBusy && <CircularProgress size={18} sx={{ color: '#C9A84C' }} />}
                    </Box>
                  )}

                  {/* Day pass credits — a promise of one pass, redeemed when
                      they choose. Redemption starts a fresh 7-day window. */}
                  <Typography variant="subtitle2" sx={{ color: '#C9A84C', fontWeight: 700, mb: 1, mt: 2 }}>
                    Day Pass Credits
                  </Typography>
                  {dayPassCredits.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
                      No credits issued.
                    </Typography>
                  )}
                  {dayPassCredits
                    .slice()
                    .sort((a, b) => (b.issuedAt?.seconds || 0) - (a.issuedAt?.seconds || 0))
                    .map(c => (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                          {c.reason || 'Day pass credit'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          Issued {fmtD(c.issuedAt)}{c.issuedByName ? ` by ${c.issuedByName}` : ''}
                          {c.status === 'redeemed' ? ` \u00b7 redeemed ${fmtD(c.redeemedAt)}` : ''}
                        </Typography>
                      </Box>
                      <Chip size="small" label={c.status}
                        sx={{ textTransform: 'capitalize', fontWeight: 600,
                          backgroundColor: c.status === 'available' ? 'rgba(39,174,96,0.3)'
                            : c.status === 'redeemed' ? 'rgba(127,140,141,0.3)' : 'rgba(192,57,43,0.3)',
                          color: c.status === 'available' ? '#A9DFBF'
                            : c.status === 'redeemed' ? '#BDC3C7' : '#F5B7B1' }} />
                      {c.status === 'available' && canEditPeople && (
                        <>
                          <Button size="small" disabled={creditBusy}
                            onClick={() => creditAction('redeemDayPassCredit', c.id, 'Credit redeemed \u2014 a day pass for today starts now.')}
                            sx={{ color: '#C9A84C' }}>Redeem</Button>
                          <Button size="small" color="error" disabled={creditBusy}
                            onClick={() => creditAction('voidDayPassCredit', c.id, 'Credit voided.')}>Void</Button>
                        </>
                      )}
                    </Box>
                  ))}
                  {canEditPeople && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, mb: 1 }}>
                    <TextField size="small" fullWidth placeholder="Reason (optional) \u2014 e.g. rained out 8/3"
                      value={creditReason} onChange={e => setCreditReason(e.target.value)}
                      sx={{ '& .MuiInputBase-input': { color: 'white' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' } }} />
                    <Button size="small" variant="outlined" disabled={creditBusy} onClick={issueCredit}
                      sx={{ whiteSpace: 'nowrap', color: '#C9A84C', borderColor: '#C9A84C' }}>
                      Issue credit
                    </Button>
                  </Box>
                  )}

                  <Typography variant="subtitle2" sx={{ color: '#C9A84C', fontWeight: 700, mb: 1, mt: 2 }}>Purchases</Typography>
                  {myPurchases.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      No purchase records for this user. (Subscription renewals bill in Stripe without creating purchase records.)
                    </Typography>
                  )}
                  {myPurchases.slice(0, 15).map(p => (
                    <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {p.tierName || p.planName || p.description || p.purchaseType || 'Purchase'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', ml: 2 }}>
                        {p.amount != null ? `$${Number(p.amount).toFixed(2)}` : (p.amountPaid != null ? `$${Number(p.amountPaid).toFixed(2)}` : '')} {fmtD(p.purchaseDate || p.createdAt || p.timestamp)}
                      </Typography>
                    </Box>
                  ))}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setMemberDetailDialog(false)} sx={{ color: '#C9A84C' }}>Close</Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

    </Layout>
  );
}

export default People;
